/**
 * Sistema de internacionalización (i18n) para inglés y español
 */

import { warn as logWarn } from './logger';

const translations = {
  es: {
    // Navegación
    nav: {
      brand: 'Es tu mejor opción',
      home: 'Inicio',
      allNews: 'Todas las noticias',
      blogAdmin: 'Dashboard',
      login: 'Iniciar sesión',
      account: 'Mi cuenta',
      panel: 'Panel',
      menuToggleAria: 'Abrir o cerrar menú de navegación'
    },
    blogAdmin: {
      groqSectionTitle: 'Groq (generación de artículos con IA)',
      groqSectionHelp:
        'Guarda tu API key en este panel para usar “Generar con IA” en artículos. Las claves de IA no se leen desde variables de entorno del servidor; solo desde la base de datos.',
      saveGroqKey: 'Guardar clave',
      groqKeySaved: 'Clave guardada correctamente.',
      groqKeyError: 'No se pudo guardar la clave.',
      checkGroqQuota: 'Comprobar cuota',
      groqQuotaLoading: 'Comprobando…',
      groqQuotaUnavailable: 'Sin datos de cuota',
      groqQuotaOk: 'Groq disponible',
      groqQuotaOkRemaining: 'Quedan {remaining} de {limit} solicitudes disponibles',
      groqQuotaOkRemainingPartial: 'Quedan {remaining} solicitudes (límite no informado por la API)',
      groqQuota401Hint:
        '401 no autorizado: la clave es inválida, revocada o no coincide. Con Groq como proveedor activo se usa la clave del panel; también aplica la columna groq_api_key del usuario. Genera una clave nueva (gsk_…) en console.groq.com, pégala sin espacios ni comillas y guarda de nuevo.',
      groqQuotaLimitReached: 'Groq alcanzó el límite de solicitudes (revisa retry si la API lo indicó).',
      groqQuotaPaymentRequired: 'Groq requiere pago o el plan gratuito se agotó.',
      groqQuotaNotConfigured: 'No hay API key de Groq configurada.',
      groqQuotaVerifyFailed: 'No se pudo verificar la cuota de Groq.',
      catalogSectionTitle: 'Catálogo de productos',
      catalogSectionHelp: 'Administra categorías y productos (nombre, precio, categoría) desde aquí.',
      categoriesTitle: 'Categorías',
      productsTitle: 'Productos',
      categorySlugPlaceholder: 'Slug de categoría (ej: hosting)',
      categoryNamePlaceholder: 'Nombre de categoría',
      categoryDescPlaceholder: 'Descripción (opcional)',
      saveCategory: 'Guardar categoría',
      categorySaved: 'Categoría guardada.',
      productNamePlaceholder: 'Nombre del producto',
      productSlugPlaceholder: 'Slug del producto',
      productDescPlaceholder: 'Descripción del producto',
      pricePlaceholder: 'Precio',
      productPriceLabel: 'Precio actual (venta)',
      productComparePriceLabel: 'Precio de referencia',
      productComparePricePlaceholder: 'Ej: 100',
      productComparePriceHelp: 'Opcional. Si es mayor al precio actual, se muestra como oferta con % de descuento.',
      productCurrencyLabel: 'Moneda',
      saveProduct: 'Guardar producto',
      productSaved: 'Producto guardado.',
      productDeleted: 'Producto eliminado.',
      noProducts: 'No hay productos registrados.',
      noCategoryOption: 'Sin categoría',
      productTableName: 'Producto',
      productTableCategory: 'Categoría',
      productTablePrice: 'Precio',
      productTableEnabled: 'En catálogo',
      productActiveOn: 'Activo',
      productActiveOff: 'Inactivo',
      productFormActiveLabel: 'Mostrar en el catálogo público',
      productFormDirectCheckoutLabel: 'Mostrar botón «Pagar ahora»',
      productFormDirectCheckoutHelp:
        'Si está activo, el producto muestra checkout directo en la home y en su ficha (requiere PayPal o Binance configurados).',
      productTableDirectCheckout: 'Pagar ahora',
      viewProduct: 'Ver página del producto',
      productPreviewNote: 'Vista previa (producto inactivo)',
      productDirectCheckoutOn: 'Sí',
      productDirectCheckoutOff: 'No',
      productDirectCheckoutUpdateError: 'No se pudo actualizar el checkout directo del producto.',
      productActiveUpdateError: 'No se pudo actualizar la disponibilidad del producto.',
      productIconLabel: 'Icono del producto',
      productIconHelp: 'Se muestra centrado sobre el título en el listado de la página principal.',
      productIconPickerLabel: 'Icono',
      productIconColorLabel: 'Color',
      productIconRocket: 'Cohete (lanzamiento)',
      productIconChartLine: 'Gráfico (crecimiento)',
      productIconLayerGroup: 'Capas (escala)',
      productIconCogs: 'Engranajes (a medida)',
      productIconGlobe: 'Globo (dominios)',
      productIconCloud: 'Nube (hosting)',
      productIconServer: 'Servidor (VPS)',
      productIconAward: 'Medalla (plan)',
      productIconCrown: 'Corona (premium)',
      productIconTools: 'Herramientas',
      productIconWrench: 'Llave (mantenimiento)',
      productIconShoppingCart: 'Carrito',
      productIconWhatsapp: 'WhatsApp',
      productIconCreditCard: 'Tarjeta (pagos)',
      productIconBoxes: 'Cajas (inventario)',
      productIconFileInvoice: 'Factura',
      productIconCashRegister: 'Caja (POS)',
      productIconChartBar: 'Barras (KPI)',
      productIconBoxOpen: 'Caja genérica',
      productIconColorCyan: 'Cian',
      productIconColorSky: 'Cielo',
      productIconColorViolet: 'Violeta',
      productIconColorAmber: 'Ámbar',
      productIconColorGold: 'Oro',
      productIconColorCopper: 'Cobre',
      productIconColorSilver: 'Plata',
      productIconColorEmerald: 'Esmeralda',
      productIconColorRose: 'Rosa',
      productIconColorLime: 'Lima',
      confirmDeleteProduct: '¿Eliminar este producto?',
      catalogLoadError: 'No se pudo cargar el catálogo.',
      catalogSaveError: 'No se pudo guardar el catálogo.',
      catalogDeleteError: 'No se pudo eliminar el producto.',
      catalogRequiredFields: 'Completa los campos requeridos antes de guardar.',
      testimonialsSectionTitle: 'Testimonios de clientes',
      testimonialsSectionHelp:
        'Gestiona las reseñas que aparecen en la sección Clientes de la página principal. El país se muestra con su bandera.',
      testimonialQuoteEsLabel: 'Cita (español)',
      testimonialQuoteEnLabel: 'Cita (inglés)',
      testimonialNameLabel: 'Nombre',
      testimonialRoleEsLabel: 'Actividad / rol (español)',
      testimonialRoleEnLabel: 'Actividad / rol (inglés)',
      testimonialCountryLabel: 'País',
      testimonialInitialLabel: 'Inicial del avatar',
      testimonialSortOrderLabel: 'Orden',
      saveTestimonial: 'Guardar testimonio',
      testimonialSaved: 'Testimonio guardado.',
      testimonialDeleted: 'Testimonio eliminado.',
      noTestimonials: 'No hay testimonios registrados.',
      confirmDeleteTestimonial: '¿Eliminar este testimonio?',
      testimonialsLoadError: 'No se pudieron cargar los testimonios.',
      testimonialsSaveError: 'No se pudo guardar el testimonio.',
      testimonialsDeleteError: 'No se pudo eliminar el testimonio.',
      testimonialsRequiredFields: 'Completa cita (ES/EN) y nombre antes de guardar.',
      testimonialActiveOn: 'Visible',
      testimonialActiveOff: 'Oculto',
      testimonialActiveUpdateError: 'No se pudo actualizar la visibilidad del testimonio.',
      testimonialTableName: 'Cliente',
      testimonialTableCountry: 'País',
      testimonialTableOrder: 'Orden',
      testimonialTableEnabled: 'En home',
      countryPy: 'Paraguay',
      countryBo: 'Bolivia',
      countryPe: 'Perú',
      countryCo: 'Colombia',
      countryAr: 'Argentina',
      countryCl: 'Chile',
      countryUy: 'Uruguay',
      countryBr: 'Brasil',
      countryEc: 'Ecuador',
      countryVe: 'Venezuela',
      countryMx: 'México',
      countryCr: 'Costa Rica',
      countryPa: 'Panamá',
      countryUs: 'Estados Unidos',
      countryEs: 'España',
      cancelEdit: 'Cancelar edición',
      productPrimaryImage: 'Imagen principal (preview al compartir)',
      productPrimaryImageHelp: 'Se usa en WhatsApp, Facebook y otras redes al compartir el enlace del producto. Recomendado: 1200×630 px.',
      productGalleryImages: 'Galería del producto',
      setAsPrimaryImage: 'Usar como principal',
      removeImage: 'Quitar',
      productImageUploadError: 'No se pudo subir la imagen.',
      cardPostsTitle: 'Gestión de artículos',
      cardPostsDesc: 'Crear, editar, publicar y posicionar contenido de negocio digital.',
      cardAiTitle: 'Motor IA',
      cardAiDesc: 'Configurar proveedor, modelo y credenciales para contenido comercial.',
      cardSettingsTitle: 'Página principal y sitio',
      cardSettingsDesc: 'Activar secciones de la home, tema visual y layouts por bloque.',
      cardCatalogTitle: 'Catálogo comercial',
      cardCatalogDesc: 'Administrar categorías, productos y precios.',
      cardSalesTitle: 'Cotizador de ventas',
      cardSalesDesc: 'Wizard comercial para estimar alcance, complejidad y rango de inversión.',
      cardWhatsappTitle: 'WhatsApp',
      cardWhatsappDesc: 'Conecta una línea, escanea el QR y chatea con clientes desde el panel.',
      quickStatusWhatsapp: 'WhatsApp',
      welcomeTitle: 'Panel de gestión comercial',
      welcomeDesc: 'Administra contenido, IA, apariencia y catálogo para mejorar conversión y presencia digital.',
      quickStatusContent: 'Contenido',
      quickStatusAi: 'IA',
      quickStatusSite: 'Sitio',
      quickStatusCatalog: 'Catálogo',
      quickStatusSales: 'Cotizador',
      quoteWizardTitle: 'Wizard de cotización comercial',
      quoteWizardHelp: 'Usa este flujo para estimaciones rápidas más precisas en ventas consultivas.',
      quoteStepScope: 'Alcance',
      quoteStepComplexity: 'Complejidad',
      quoteStepResult: 'Resultado',
      quoteProjectType: 'Tipo de proyecto',
      quoteProjectWebsite: 'Sitio web',
      quoteProjectSoftware: 'Software a medida',
      quoteWebsiteType: 'Tipo de sitio web',
      quoteWebsiteLanding: 'Landing',
      quoteWebsiteCorporate: 'Corporativo',
      quoteWebsiteEcommerce: 'E-commerce',
      quoteModules: 'Cantidad de módulos',
      quoteIntegrations: 'Cantidad de integraciones',
      quoteComplexity: 'Complejidad técnica',
      quoteComplexityLow: 'Baja',
      quoteComplexityMedium: 'Media',
      quoteComplexityHigh: 'Alta',
      quoteTimeline: 'Velocidad de entrega',
      quoteTimelineStandard: 'Estándar',
      quoteTimelineFast: 'Acelerada',
      quoteTimelineUrgent: 'Urgente',
      quoteSupportMonths: 'Meses de soporte',
      quoteAutomation: 'Incluye automatizaciones',
      quoteSeo: 'Incluye SEO inicial',
      quoteUxResearch: 'Incluye discovery UX',
      quoteEstimatedRange: 'Rango estimado',
      quoteComplexityScore: 'Score de complejidad',
      quoteAssumptionOne: 'Estimación basada en alcance funcional inicial y supuestos estándar.',
      quoteAssumptionTwo: 'No incluye costos de terceros (licencias, pasarelas o infraestructura enterprise).',
      quoteAssumptionThree: 'Ajustar en propuesta final luego de discovery técnico y comercial.',
      quoteBack: 'Atrás',
      quoteNext: 'Siguiente',
      quoteCalculate: 'Calcular rango',
      quoteRestart: 'Reiniciar wizard',
      quoteSave: 'Guardar cotización',
      quoteSaved: 'Cotización guardada correctamente.',
      quoteSaveError: 'No se pudo guardar la cotización.',
      quoteStageUpdated: 'Etapa actualizada correctamente.',
      quoteStageUpdateError: 'No se pudo actualizar la etapa.',
      quotePipelineTitle: 'Pipeline comercial',
      quotePipelineRefresh: 'Actualizar pipeline',
      quotePipelineLoading: 'Cargando pipeline...',
      quotePipelineEmpty: 'No hay cotizaciones registradas todavía.',
      quoteTableId: 'ID',
      quoteTableProject: 'Proyecto',
      quoteTableRange: 'Rango',
      quoteTableStage: 'Etapa',
      quoteTableOwner: 'Responsable',
      quoteTableNotes: 'Notas',
      quoteFilterAllStages: 'Todas las etapas',
      quoteFilterAllProjects: 'Todos los proyectos',
      quoteFilterAllOwners: 'Todos los responsables',
      quoteFilterSearch: 'Buscar por contacto, empresa o notas...',
      quoteUnassigned: 'Sin asignar',
      quoteNotesPlaceholder: 'Seguimiento comercial...',
      quoteUpdated: 'Cotización actualizada.',
      quoteUpdateError: 'No se pudo actualizar la cotización.',
      quoteStageNew: 'Nuevo',
      quoteStageQualified: 'Calificado',
      quoteStageProposal: 'Propuesta',
      quoteStageWon: 'Ganado',
      quoteStageLost: 'Perdido',
      quoteExportCsv: 'Exportar CSV',
      quoteExportCrm: 'Exportar CRM JSON',
      quoteExportCsvOk: 'Exportación CSV generada.',
      quoteExportCrmOk: 'Exportación CRM generada.',
      quoteExportError: 'No se pudo exportar la data de cotizaciones.',
      quoteRunReminders: 'Ejecutar recordatorios',
      quoteRemindersRunOk: 'Recordatorios ejecutados',
      quoteRemindersRunError: 'No se pudieron ejecutar los recordatorios.',
      contentTypeBusiness: 'Business',
      contentTypeTech: 'Tech',
      contentTypeNews: 'News',
      templateActionsTitle: 'Plantillas comerciales rápidas',
      templateApplyCaseStudy: 'Aplicar plantilla business',
      templateApplyStrategy: 'Aplicar plantilla tech',
      templateApplyTactical: 'Aplicar plantilla news',
      templateAppliedMessage: 'Plantilla aplicada al borrador.',
      templateContentCaseStudy: '<h2>Contexto del negocio y objetivo</h2><p>Describe el escenario comercial, segmento objetivo y resultado esperado.</p><h2>Fricciones actuales</h2><ul><li>Principal bloqueo de conversión</li><li>Brecha de visibilidad o confianza</li><li>Cuello de botella operativo</li></ul><h2>Hoja de ruta de ejecución</h2><ol><li>Acciones de corto plazo (0-2 semanas)</li><li>Mejoras de mediano plazo (3-6 semanas)</li><li>Cadencia de monitoreo KPI</li></ol><h2>Impacto esperado</h2><p>Resume calidad de leads, mejora de conversión y ganancias operativas.</p>',
      templateContentStrategy: '<h2>Tesis estratégica</h2><p>Define por qué esta estrategia importa hoy y qué métrica comercial debe mover.</p><h2>Framework central</h2><ul><li>Posicionamiento y arquitectura de mensaje</li><li>Canales de adquisición</li><li>Capas de conversión y confianza</li></ul><h2>Checklist de implementación</h2><ul><li>Responsables y timeline</li><li>Activos requeridos</li><li>Setup de tracking y atribución</li></ul><h2>Criterios de decisión</h2><p>Aclara qué señales indican escalar, iterar o frenar.</p>',
      templateContentTactical: '<h2>Objetivo táctico rápido</h2><p>Define la meta de conversión inmediata de esta táctica.</p><h2>Ejecución paso a paso</h2><ol><li>Preparación (herramientas y línea base)</li><li>Acciones de lanzamiento</li><li>Bucle de optimización</li></ol><h2>Ejemplos de copy</h2><ul><li>Variante A de CTA principal</li><li>Variante B de CTA principal</li><li>Mensaje de seguimiento</li></ul><h2>Métricas a monitorear</h2><p>CTR, tasa de finalización del lead y conversaciones calificadas para venta.</p>',
      aiSectionTitle: 'Configuración de IA',
      aiSectionHelp: 'Define proveedor, modelo, región y credenciales para generación de contenido.',
      siteSettingsTitle: 'Configuraciones generales del sitio',
      siteSettingsHelp: 'Define template/theme y variaciones visuales por sección (hero, ofertas y CTA).',
      saveSiteSettings: 'Guardar configuración del sitio',
      siteSettingsSaved: 'Configuración del sitio guardada correctamente.',
      saveContactSettings: 'Guardar contacto público',
      contactSettingsSaved: 'Contacto público guardado correctamente.',
      contactSettingsSaveError: 'No se pudo guardar el contacto público.',
      postSavedSuccess: 'Artículo guardado correctamente.',
      postDeletedSuccess: 'Artículo eliminado correctamente.',
      postGeneratedSuccess: 'Borrador generado correctamente con IA.',
      generateTopicRequired: 'Escribe un tema para generar el artículo.',
      postRequiredFields: 'Título y contenido son obligatorios.',
      aiProviderLabel: 'Proveedor IA',
      aiModelLabel: 'Modelo',
      aiRegionLabel: 'Región',
      aiApiKeyLabel: 'API key del proveedor',
      aiTimeoutLabel: 'Timeout proveedor (ms)',
      aiTemperatureLabel: 'Temperatura IA',
      aiFallbackOrderLabel: 'Orden de fallback',
      aiFallbackOrderHelp: 'Si falla el proveedor primario, se intentará en este orden.',
      providerHealthRefresh: 'Actualizar health proveedores',
      providerHealthLoading: 'Verificando health...',
      providerMatrixTitle: 'Matriz de proveedores',
      providerMatrixProvider: 'Proveedor',
      providerMatrixModel: 'Modelo',
      providerMatrixTimeout: 'Timeout',
      providerMatrixStatus: 'Estado',
      providerPrimaryBadge: 'Primario',
      providerStatusAvailable: 'Disponible',
      providerStatusUnavailable: 'No disponible',
      uiThemeLabel: 'Template / Theme',
      uiThemeHelp: 'Selecciona el template visual activo para la landing y bloques comerciales.',
      themeAurora: 'Aurora',
      themeSunset: 'Sunset',
      themeEmerald: 'Emerald',
      uiHeroLayoutLabel: 'Layout Hero',
      uiOffersLayoutLabel: 'Layout Ofertas',
      uiCtaLayoutLabel: 'Layout CTA',
      uiLayoutDefault: 'Default',
      uiLayoutCompact: 'Compacto',
      uiLayoutSpotlight: 'Spotlight',
      uiLayoutCards: 'Cards resaltadas',
      uiLayoutMinimal: 'Minimal',
      uiLayoutSplit: 'Split',
      uiLayoutBanner: 'Banner',
      resetSiteDefaults: 'Restablecer defaults',
      siteSettingsDefaultsApplied: 'Se aplicaron valores por defecto (theme + layouts). Guarda para persistirlos.',
      landingSectionsTitle: 'Secciones visibles en la página principal',
      landingSectionsHelp: 'Activa o desactiva bloques de la landing. Los cambios aplican al sitio público para todos los visitantes.',
      landingSectionHero: 'Hero principal (titular y CTAs)',
      landingSectionEncajaBanner: 'Banner Encaja (producto estrella)',
      landingSectionHeroStats: 'Franja de estadísticas bajo el hero',
      landingSectionDomainSearch: 'Buscador de dominios',
      landingSectionServices: 'Servicios y formulario de contacto',
      landingSectionOffers: 'Catálogo de productos / paquetes',
      landingSectionConversionProof: 'Prueba de conversión (bloque bajo productos)',
      landingSectionProcess: 'Proceso de trabajo',
      landingSectionWhyChoose: 'Por qué elegirnos',
      landingSectionTestimonials: 'Testimonios de clientes',
      landingSectionNews: 'Últimas noticias',
      landingSectionFaq: 'Preguntas frecuentes',
      landingSectionFinalCta: 'CTA final',
      landingSectionWhatsappFab: 'Botón flotante de WhatsApp',
      landingSectionMobileStickyCta: 'Barra fija móvil (WhatsApp / login)',
      landingSectionFooter: 'Pie de página',
      landingSectionsDefaultsApplied: 'Se restauró el layout compacto (dominios + productos + noticias). Guarda para persistir.',
      landingSectionsResetCompact: 'Layout compacto',
      languageSelectorEnabledLabel: 'Mostrar selector de idioma (ES / EN)',
      languageSelectorEnabledHelp:
        'Desactivado: el sitio se muestra solo en español y se oculta el selector del menú. Actívalo cuando quieras ofrecer inglés.',
      saveLandingSections: 'Guardar secciones de la home',
      siteSettingsThemeBlockTitle: 'Tema y layouts visuales',
      siteSettingsThemeBlockHelp: 'Ajustes de apariencia global. También se guardan con el botón de arriba.',
      themeAccessibilityTitle: 'Chequeo rápido de accesibilidad (AA)',
      themeAccessibilityPass: 'Contraste base OK para lectura en el tema actual.',
      themeAccessibilityWarn: 'Se detectó contraste bajo en al menos una combinación base.',
      themeCheckprimaryOnSurface: 'Primario sobre superficie',
      themeChecktextOnSurface: 'Texto base sobre superficie',
      contactSettingsTitle: 'Contacto público (superadmin)',
      contactSettingsHelp: 'WhatsApp, email y notas de soporte visibles en la landing.',
      contactWhatsappPyLabel: 'WhatsApp Paraguay',
      contactWhatsappBoLabel: 'WhatsApp Bolivia',
      contactWhatsappPyPlaceholder: '595983062031',
      contactWhatsappBoPlaceholder: '59177499919',
      contactEmailLabel: 'Email de contacto',
      contactEmailPlaceholder: 'contacto@estumejoropcion.com',
      contactSupportEsLabel: 'Nota de soporte (ES)',
      contactSupportEnLabel: 'Nota de soporte (EN)',
      contactSupportEsPlaceholder: 'Atención en inglés por mensaje.',
      contactSupportEnPlaceholder: 'English support is available by conversation.'
    },
    whatsappAdmin: {
      loading: 'Cargando WhatsApp…',
      errorGeneric: 'Ocurrió un error.',
      notConfiguredServer: 'El servidor no tiene configurado WHATSAPPAPI_SERVER y WHATSAPP_TOKEN. Pide al administrador del hosting que los agregue al .env.',
      tabConnection: 'Conexión',
      tabChats: 'Conversaciones',
      connectionTitle: 'Línea de WhatsApp',
      connectionHelp: 'Registra un número nuevo o reconecta la línea activa. Escanea el QR con el teléfono que usará esta línea.',
      refresh: 'Actualizar',
      lineIdLabel: 'ID de línea: {id}',
      phoneLabel: 'Teléfono: {phone}',
      registerNewLine: 'Registrar nuevo número',
      startSession: 'Iniciar sesión',
      newQr: 'Nuevo QR',
      disconnect: 'Desconectar',
      disconnectConfirm: '¿Desconectar esta sesión de WhatsApp?',
      disconnected: 'Sesión desconectada.',
      noLineYet: 'No hay línea activa. Crea una nueva para empezar.',
      selectLine: 'Línea activa para este sitio',
      lineCreated: 'Línea creada. Escanea el QR si aparece.',
      sessionStarted: 'Sesión iniciada.',
      conversations: 'Chats',
      noConversations: 'Aún no hay mensajes entrantes en los últimos 7 días.',
      selectConversation: 'Elige una conversación para ver el historial.',
      messagePlaceholder: 'Escribe un mensaje…',
      send: 'Enviar',
      createLineTitle: 'Registrar nueva línea',
      lineNameLabel: 'Nombre de la línea',
      lineNamePlaceholder: 'Ej. EstuMejorOpcion Ventas',
      cancel: 'Cancelar',
      createAndStart: 'Crear e iniciar',
      scanQrTitle: 'Escanea el código QR',
      scanQrHelp: 'En WhatsApp del teléfono: Dispositivos vinculados → Vincular dispositivo.',
      unknownContact: 'Contacto',
      multiLineTitle: 'Líneas de WhatsApp',
      multiLineHelp: 'Registra una línea por país o canal. Cada una tiene un identificador (ej. paraguay, bolivia) para chatear por separado.',
      noLinesRegistered: 'No hay líneas registradas en este panel.',
      lineIdSlugLabel: 'Identificador (slug)',
      lineIdSlugPlaceholder: 'paraguay',
      lineIdSlugHelp: 'Solo minúsculas, números, guiones. Ej: paraguay, bolivia',
      lineLabelField: 'Etiqueta visible',
      lineLabelPlaceholder: 'Paraguay',
      lineNameWhaticketHelp: 'Nombre en Whaticket (interno).',
      presetParaguay: 'Plantilla Paraguay',
      presetBolivia: 'Plantilla Bolivia',
      chatAsLine: 'Chatear como línea',
      conversationsFor: 'Chats — {label}',
      chatLineNotConnected: 'Esta línea no está conectada. Ve a Conexión e inicia sesión.',
      showQr: 'Ver QR',
      removeFromPanel: 'Quitar del panel',
      openChats: 'Abrir conversaciones →',
      scanQrFor: 'QR — {label}',
      sessionStartedFor: 'Sesión iniciada: {label}',
      disconnectConfirmFor: '¿Desconectar la línea {label}?',
      disconnectedFor: 'Desconectado: {label}',
      removeLineConfirm: '¿Quitar {label} del panel? (No borra la línea en Whaticket)',
      newConversation: 'Nueva conversación',
      newConversationTitle: 'Iniciar conversación',
      newConversationNumber: 'Número de WhatsApp',
      newConversationNumberPlaceholder: '59170000000',
      newConversationNumberHelp: 'Código de país + número, solo dígitos. Ej: 59177490191 (Bolivia) o 595981234567 (Paraguay).',
      newConversationIsGroup: 'Es un grupo de WhatsApp',
      newConversationStart: 'Abrir chat',
      newChatHint: 'Nueva conversación — escribe el primer mensaje',
      messageSent: 'Mensaje enviado.',
      missingPhoneForSend: 'No se pudo identificar el número de este chat. Usa «Nueva conversación» e ingresa el número manualmente.',
      invalidPhoneNumber: 'Ingresa un número válido (mínimo 8 dígitos, con código de país).',
      qrExpiresHint: 'El código se renueva solo cada ~20 segundos. Si WhatsApp dice «inválido», espera a que cambie en pantalla y escanea el nuevo. Vincula una línea a la vez.',
      lineConnected: 'Línea {label} conectada correctamente.',
      linkOneLineHint: 'Para vincular: elige una línea, pulsa «Iniciar sesión» y escanea cuando aparezca el QR. No abras QR de Paraguay y Bolivia al mismo tiempo.',
      errWappCheckContact: 'No se pudo verificar el número con WhatsApp. Comprueba que la línea esté conectada (verde), que el número tenga código de país y que exista en WhatsApp.',
      errWappInvalidContact: 'Ese número no tiene WhatsApp o el formato es incorrecto. Usa solo dígitos con código de país (ej. 59177490191).',
      errWappNotInitialized: 'La línea de WhatsApp aún no está lista. Ve a Conexión, espera a que quede «Conectada» y vuelve a intentar.',
      errWappDisabled: 'Esta línea está deshabilitada en Whaticket.',
      sendTimeout: 'El envío tardó demasiado. La línea puede estar reconectando; espera unos segundos e intenta de nuevo.',
      sendingMessage: 'Enviando…',
      attachFile: 'Adjuntar archivo',
      removeAttachment: 'Quitar',
      messageOrMediaRequired: 'Escribe un mensaje o adjunta un archivo.',
      previewImage: '📷 Imagen',
      previewAudio: '🎤 Audio',
      previewVideo: '🎬 Video',
      previewDocument: '📄 Documento',
      previewFile: '📎 Archivo',
      previewLink: '🔗 Enlace',
      openFile: 'Abrir archivo',
      emptyMessage: '(mensaje vacío)',
    },
    userAuth: {
      pageTitle: 'Cuenta',
      pageSubtitle: 'Inicia sesión o regístrate. Nuevos usuarios reciben el rol client.',
      loginTitle: 'Iniciar sesión',
      signupTitle: 'Crear cuenta',
      email: 'Correo electrónico',
      emailPlaceholder: 'tu@email.com',
      nameOptional: 'Nombre (opcional)',
      namePlaceholder: 'Tu nombre',
      password: 'Contraseña',
      passwordPlaceholder: 'Tu contraseña',
      passwordCreatePlaceholder: 'Mínimo 6 caracteres',
      confirmPassword: 'Confirmar contraseña',
      confirmPasswordPlaceholder: 'Repite la contraseña',
      loginSubmit: 'Entrar',
      signupSubmit: 'Registrarme',
      noAccountSignup: '¿No tienes cuenta? Crear cuenta',
      haveAccountLogin: '¿Ya tienes cuenta? Iniciar sesión',
      or: 'O',
      continueWithGoogle: 'Continuar con Google',
      showPassword: 'Mostrar contraseña',
      hidePassword: 'Ocultar contraseña',
      errorPasswordMismatch: 'Las contraseñas no coinciden.',
      errorPasswordShort: 'La contraseña debe tener al menos 6 caracteres.',
      errorSignInGeneric: 'No se pudo iniciar sesión.',
      errorRegisterGeneric: 'No se pudo completar el registro.',
      dashboardTitle: 'Mi cuenta',
      dashboardSubtitle: 'Revisa tus compras y ofertas especiales.',
      purchasesTitle: 'Compras realizadas',
      offersTitle: 'Ofertas especiales',
      noPurchases: 'Aún no tienes compras registradas.',
      noOffers: 'No hay ofertas activas por ahora.',
      offersDiscountHint: 'Productos del catálogo con precio de referencia y descuento aplicado.',
      viewOffer: 'Ver oferta',
      offerPayNow: 'Pagar ahora',
      personalCouponsTitle: 'Cupones y promos personalizadas',
      goToAdmin: 'Ir al panel de gestión',
      logout: 'Cerrar sesión'
    },
    checkoutDomain: {
      title: 'Pago de registro de dominio',
      intro:
        'Revisamos precio y disponibilidad al momento del pago. Tras confirmar el pago, registramos tu pedido en la cuenta para completar el registro y DNS.',
      backHome: 'Volver al inicio',
      missingDomain: 'Falta el dominio en la URL. Vuelve al inicio y elige una opción en la tabla.',
      loginRequiredTitle: 'Inicia sesión para continuar',
      loginRequiredBody: 'Necesitas una cuenta para pagar y guardar tu compra.',
      goToLogin: 'Ir a iniciar sesión',
      loadingQuote: 'Obteniendo cotización…',
      labelDomain: 'Dominio',
      labelAmount: 'Total',
      amountUsd: 'USD {amount}',
      quoteDisclaimer:
        'Este es el precio de venta aplicado para tu compra de dominio.',
      payWithPayPal: 'Pagar con PayPal',
      payWithBinanceWallet: 'Pagar con depósito USDT (Binance)',
      noGateways: 'Los métodos de pago no están configurados en el servidor.',
      payPalCancelled: 'Cancelaste el pago en PayPal. Puedes intentar de nuevo.',
      binanceInstructionsTitle: 'Envía USDT a esta dirección de depósito',
      binanceScanQr: 'Escanea el código QR con Binance u otra wallet compatible',
      binanceDownloadQr: 'Descargar QR',
      binanceQrHint: 'También puedes guardar la imagen y escanearla desde la galería de tu teléfono.',
      binanceReference: 'Referencia interna:',
      binanceNetwork: 'Red:',
      binanceAmount: 'Monto exacto (USDT):',
      binanceAmountHint:
        'Equivale aproximado a tu compra en USD ({usd}). Debe coincidir con el monto mostrado para asociar el pago.',
      binanceAddress: 'Dirección:',
      binanceTag: 'Memo / Tag:',
      binancePolling: 'Detectamos el depósito automáticamente. Esta página se actualiza cuando llegue.',
      successTitle: 'Pago confirmado',
      successBody: 'Hemos registrado tu compra para {domain}.',
      fulfillmentNote:
        'El registro del dominio en el proveedor y la configuración DNS pueden requerir un paso manual; te contactamos si hace falta.',
      goToAccount: 'Ir a mi cuenta',
      errorQuote: 'No se pudo obtener la cotización. Intenta de nuevo.',
      errorPriceUnavailable: 'No hay precio disponible para este dominio en este momento.',
      errorNotAvailable: 'Este dominio no está disponible para registro.',
      errorPayPalStart: 'No se pudo iniciar PayPal.',
      errorPayPalCapture: 'No se pudo confirmar el pago en PayPal.',
      errorBinanceStart: 'No se pudo preparar la dirección de depósito.',
      errorBinancePoll: 'Aún no detectamos el depósito.'
    },
    // Recomendaciones
    recommendations: {
      title: 'Recomendaciones',
      loading: 'Cargando recomendaciones...',
      loadingMovies: 'Cargando películas...',
      loadingSeries: 'Cargando series...',
      loadingMusic: 'Cargando música...',
      noRecommendations: 'No tienes recomendaciones aún',
      welcomeNoRecommendations: '¡Bienvenido! Aún no tienes recomendaciones',
      noRecommendationsOfType: 'No tienes recomendaciones de {type} aún',
      generateFirst: 'Para empezar, puedes generar tus primeras recomendaciones basadas en tus preferencias y contenido de otros usuarios.',
      generateType: 'Puedes generar recomendaciones de {type} basadas en tus preferencias y contenido de otros usuarios.',
      generateFirstButton: 'Generar recomendaciones',
      generateTypeButton: 'Generar recomendaciones de {type}',
      generating: 'Generando recomendaciones...',
      generatingFirst: 'Generando…',
      generateFirstRecommendations: 'Generar primeras recomendaciones',
      moviesRecommended: 'Películas Recomendadas',
      seriesRecommended: 'Series Recomendadas',
      musicRecommended: 'Música Recomendada',
      moviesAvailable: '{count} películas disponibles',
      seriesAvailable: '{count} series disponibles',
      musicAvailable: '{count} canciones/álbumes disponibles',
      noMoviesAvailable: 'No hay películas disponibles',
      noSeriesAvailable: 'No hay series disponibles',
      noMusicAvailable: 'No hay música disponible',
      noMoviesFound: 'No se encontraron películas recomendadas. Intenta actualizar la página.',
      noSeriesFound: 'No se encontraron series recomendadas. Intenta actualizar la página.',
      noMusicFound: 'No se encontró música recomendada. Intenta actualizar la página.',
      watched: 'Vista',
      listened: 'Escuchada',
      watchedOn: 'Vista el:',
      listenedOn: 'Escuchada el:',
      yourRating: 'Tu calificación:',
      rating: 'Calificación:',
      markAsWatched: 'Marcar como Vista',
      markAsListened: 'Marcar como Escuchada',
      alreadyWatched: 'Ya Vista',
      alreadyListened: 'Ya Escuchada',
      seasons: 'temporadas',
      retry: 'Reintentar',
      noResultsWithFilters: 'No se encontraron recomendaciones con los filtros aplicados.',
      searchMusicTitle: 'Buscar música para marcar como escuchada',
      searchMusicDescription: 'Busca canciones por nombre/artista o explora por género (cumbia, rock, pop, reggaeton, etc.). Márcalas como escuchadas y se regenerarán las recomendaciones con la IA.',
      searchMoviesTitle: 'Buscar {type} para marcar como vistas',
      searchMoviesDescription: 'Encuentra una película o serie, márcala como vista con tu calificación y se regenerarán las recomendaciones con la IA.',
      results: 'Resultados',
      allGenresOption: 'Todos los géneros',
      markAsWatched: 'Marcar como Visto',
      omit: 'Omitir',
      reactivate: 'Reactivar',
      omitted: 'Omitida',
      new: 'Nueva',
      searchPlaceholder: 'Buscar recomendaciones...',
      allTypes: 'Todos los tipos',
      movies: 'Películas',
      series: 'Series',
      music: 'Música',
      allGenres: 'Todos los géneros',
      filteringBy: 'Filtrando por',
      active: 'Activas',
      omitted: 'Omitidas',
      all: 'Todas',
      favorites: 'Favoritos',
      favoritesActive: 'Favoritos Activos',
      favoritesOmitted: 'Favoritos Omitidos',
      addToFavorites: 'Agregar a favoritos',
      removeFromFavorites: 'Quitar de favoritos',
      sortBy: 'Ordenar por',
      averageRating: 'Calificación Promedio',
      externalRating: 'Calificación Externa',
      aiRating: 'Calificación IA',
      userRating: 'Calificación Usuario',
      externalRatingLabel: 'Calificación Externa',
      aiRatingLabel: 'Calificación IA',
      averageLabel: 'Promedio',
      artist: 'Artista',
      actors: 'Actores',
      recommendedAt: 'Recomendada',
      whyRecommended: '¿Por qué se recomienda?',
      confirmOmit: '¿Omitir "{title}"? No aparecerá en futuras generaciones de recomendaciones.',
      errorLoading: 'Error cargando las recomendaciones.',
      errorGenerating: 'Error generando recomendaciones de {type}. Verifica tu configuración de Groq en Configuración.',
      couldNotGenerate: 'No se pudieron generar recomendaciones de {type}. Verifica tu configuración de Groq en Configuración o agrega contenido visto primero.',
      refresh: 'Actualizar',
      exploreContent: 'Explora películas, series y música recomendadas. Marca como vista/escuchada y califica tu experiencia.',
      processing: 'Procesando...',
      results: 'Resultados',
      clearFilter: 'Quitar filtro',
      errorSearching: 'Error en la búsqueda',
      enterSongOrGenre: 'Ingresa un nombre de canción/artista o selecciona un género',
      movieNamePlaceholder: 'Nombre de la película...',
      seriesNamePlaceholder: 'Nombre de la serie...',
      songOrArtistPlaceholder: 'Nombre de canción o artista...',
      typeMovies: 'películas',
      typeSeries: 'series',
      typeMusic: 'música',
      typeTVSeries: 'series de TV'
    },
    // Historial
    history: {
      title: 'Historial',
      loading: 'Cargando historial...',
      errorLoading: 'Error cargando el historial',
      errorUpdatingRating: 'Error actualizando calificación',
      errorDeletingContent: 'Error eliminando contenido',
      watched: 'Vista',
      listened: 'Escuchada',
      yourRating: 'Tu calificación',
      edit: 'Editar',
      delete: 'Eliminar',
      returnToRecommendations: 'Devolver a Recomendaciones',
      confirmDelete: '¿Eliminar del historial?',
      deleteNote: 'Este contenido volverá a aparecer en las recomendaciones con tu calificación actual',
      deleteAndReturn: 'Eliminar y Devolver a Recomendaciones',
      noContent: 'No hay contenido en el historial',
      noContentWatched: 'No hay contenido {type}',
      whenYouMark: 'Cuando marques contenido como {type}, aparecerá aquí.',
      tabs: {
        movies: 'Películas',
        series: 'Series',
        music: 'Música'
      },
      manageHistory: 'Gestiona tu historial de contenido visto y escuchado. Edita calificaciones o devuelve contenido a las recomendaciones.',
      totalElements: '{count} elementos en total',
      confirmDeleteTitle: 'Confirmar Eliminación',
      confirmDeleteMessage: '¿Estás seguro de que quieres eliminar {title} de tu historial?',
      deleteNoteInfo: 'Este contenido volverá a aparecer en las recomendaciones con tu calificación actual ({rating}) y podrás marcarlo como visto nuevamente.',
      actionCannotBeUndone: 'Esta acción no se puede deshacer.',
      retry: 'Reintentar',
      notes: 'Notas',
      searchPlaceholder: 'Buscar por título, artista, director, género...',
      clearSearch: 'Limpiar búsqueda',
      noResults: 'No se encontraron resultados para "{query}"',
      showingResults: 'Mostrando {filtered} de {total}'
    },
    // Estadísticas
    stats: {
      title: 'Estadísticas',
      loading: 'Cargando estadísticas...',
      noStatsAvailable: 'No hay estadísticas disponibles',
      markContentToSeeStats: 'Marca contenido como visto para ver tus estadísticas.',
      totalWatched: 'Total visto / escuchado',
      movies: 'Películas',
      series: 'Series',
      music: 'Música escuchada',
      thisMonth: 'Este mes',
      titlesWatched: 'título(s) visto(s) / escuchado(s)',
      averageRating: 'Calificación promedio',
      fromAllWatched: 'De todo lo que marcaste como visto o escuchado',
      distributionByType: 'Distribución por tipo',
      noContentByType: 'Aún no hay contenido visto por tipo.',
      favoriteGenres: 'Géneros favoritos',
      markContentToSeeGenres: 'Marca películas, series o música como visto/escuchado para ver tus géneros favoritos.',
      yourRatings: 'Tus calificaciones (1-10)',
      noRatingsYet: 'Aún no hay calificaciones.',
      monthlyActivity: 'Actividad por mes',
      noMonthlyActivity: 'Aún no hay actividad por mes. Marca contenido como visto para ver tu historial.',
      mostListenedArtists: 'Artistas más escuchados',
      movies: 'Películas',
      series: 'Series',
      music: 'Música'
    },
    // Configuración
    settings: {
      title: 'Configuración',
      preferencesTitle: 'Configuración de Preferencias',
      language: 'Idioma',
      languageDescription: 'Cambia el idioma de la interfaz y los prompts de la IA',
      spanish: 'Español',
      english: 'English',
      preferences: 'Preferencias',
      favoriteGenres: 'Géneros Favoritos',
      favoriteGenresExample: '(ej: Acción, Drama, Comedia, Terror)',
      favoriteGenresDescription: 'Categorías temáticas que te gustan (Acción, Drama, Comedia, Terror, etc.)',
      favoriteActors: 'Actores Favoritos',
      preferredMovieTypes: 'Tipos de Películas Preferidos',
      preferredMovieTypesExample: '(ej: Blockbuster, Indie, Clásicas)',
      preferredMovieTypesDescription: 'Estilo o tipo de producción que prefieres (Blockbuster, Indie, Clásicas, Documentales, etc.)',
      addGenre: 'Añadir género',
      addActor: 'Añadir actor',
      add: 'Agregar',
      actorNamePlaceholder: 'Nombre del actor',
      genreNamePlaceholder: 'Nombre del género',
      movieTypePlaceholder: 'Tipo de película',
      minimumRating: 'Calificación Mínima',
      minimumRatingDescription: 'Solo se mostrarán recomendaciones con calificación igual o superior',
      maxRecommendations: 'Máximo de Recomendaciones',
      preferredLanguage: 'Idioma Preferido',
      spanishOption: 'Español',
      englishOption: 'Inglés',
      anyLanguage: 'Cualquiera',
      contentRating: 'Clasificación de Contenido',
      contentRatingG: 'G - General',
      contentRatingPG: 'PG - Guía Parental',
      contentRatingPG13: 'PG-13 - Mayores de 13',
      contentRatingR: 'R - Restringido',
      contentRatingNC17: 'NC-17 - Solo Adultos',
      ratingWeights: 'Pesos de Calificaciones',
      userRatingWeight: 'Calificación Usuario',
      externalRatingWeight: 'Calificación Externa',
      aiRatingWeight: 'Calificación IA',
      weightsDescription: 'Los pesos deben sumar 1.0 en total. Peso actual: {total}',
      saving: 'Guardando...',
      savePreferences: 'Guardar Preferencias',
      preferencesSaved: 'Preferencias guardadas correctamente en el archivo JSON.',
      errorSaving: 'Error guardando las preferencias',
      errorLoadingQuota: 'Error cargando quota de Groq',
      aiProvider: 'Proveedor de IA',
      aiProviderTitle: 'Proveedor de IA',
      aiProviderDescription: 'Groq (nube)',
      aiProviderNote: 'Todos los usuarios utilizan Groq para generar recomendaciones personalizadas.',
      groqApiKey: 'API Key de Groq',
      groqApiKeyPlaceholder: 'Pega aquí tu API key',
      groqApiKeyConfigured: '•••••••• (ya configurada en tu cuenta)',
      noApiKey: '¿No tienes una API key?',
      getApiKey: 'Obtén una gratis en Groq',
      getApiKeyNote: '(gratis, solo necesitas crear una cuenta)',
      saveApiKey: 'Guardar API Key',
      systemStatus: 'Estado del Sistema',
      aiConnected: 'IA Conectado',
      aiDisconnected: 'IA Desconectado',
      groqQuota: 'Quota de Groq',
      checkingQuota: 'Verificando quota de Groq...',
      groqAvailable: 'Groq disponible',
      requestsRemaining: 'Quedan {remaining} requests disponibles{limit}',
      noRequestsAvailable: 'Sin requests disponibles',
      groqNotAvailable: 'Groq no disponible',
      retryAfter: 'Intenta de nuevo en {seconds} segundos',
      paymentRequired: 'Se requiere actualizar el plan de pago de Groq',
      updateQuotaToSee: 'Haz clic en "Actualizar quota" para ver tu disponibilidad',
      configureApiKeyToSee: 'Configura tu API key de Groq para ver tu quota disponible',
      checking: 'Verificando...',
      updateQuota: 'Actualizar quota',
      noAIServiceNote: 'Sin servicio de IA no se podrán generar nuevas recomendaciones automáticas.',
      preferencesSummary: 'Resumen de Preferencias',
      actors: 'Actores',
      genres: 'Géneros',
      movieTypes: 'Tipos de películas',
      minimumRatingLabel: 'Calificación mínima',
      maxRecommendationsLabel: 'Máx. recomendaciones',
      languageLabel: 'Idioma',
      ratingLabel: 'Clasificación'
    },
    // Modal de calificación
    ratingModal: {
      rateContent: 'Calificar contenido',
      editRating: 'Editar Calificación',
      rate: 'Calificar',
      howWouldYouRate: '¿Cómo calificarías este contenido?',
      ratingMethod: 'Método de calificación',
      slider: 'Deslizador',
      stars: 'Estrellas',
      buttons: 'Botones',
      thumbs: 'Pulgares',
      notes: 'Notas (opcional)',
      save: 'Guardar',
      cancel: 'Cancelar',
      markAsWatched: 'Marcar como Visto',
      genre: 'Género principal',
      selectGenre: 'Seleccionar género',
      selectGenreAria: 'Seleccionar género musical',
      selectGenrePlaceholder: '— Seleccionar género —',
      correctGenre: 'Corrige o añade el género si la API puso "Varios".',
      selectCorrectGenre: 'Elige el género en la lista. Si la API puso "Varios", selecciona el correcto.',
      contentInfo: 'Información del Contenido',
      type: 'Tipo',
      genreLabel: 'Género',
      year: 'Año',
      artist: 'Artista',
      director: 'Director',
      actors: 'Actores',
      rating: 'Calificación',
      ratingValue: 'Calificación: {value}/10',
      selectRating: 'Selecciona una calificación',
      veryBad: 'Muy malo',
      bad: 'Malo',
      regular: 'Regular',
      good: 'Bueno',
      excellent: 'Excelente',
      didYouLike: '¿Te gustó?',
      liked: 'Me gustó',
      disliked: 'No me gustó',
      description: 'Descripción',
      whyRecommended: '¿Por qué se recomienda?',
      notesPlaceholder: 'Comparte tu opinión sobre este contenido...'
    },
    // Landing Page
    landing: {
      brandName: 'Es tu mejor opción',
      navServices: 'Servicios',
      navEncaja: 'Encaja',
      navProducts: 'Productos',
      navNews: 'Noticias',
      navDomain: 'Dominio',
      navProcess: 'Proceso',
      navWhy: 'Por qué nosotros',
      navClients: 'Clientes',
      navFaq: 'FAQ',
      navWhatsAppCta: 'WhatsApp',
      encajaBadge: '⭐ Producto estrella',
      encajaByLine: 'Un producto de Estumejoropcion',
      encajaBrandName: 'Encaja',
      encajaTagline: 'Donde tu negocio encaja',
      encajaDescription:
        'Inventario, clientes, ventas y facturación en un solo panel — pensado para pymes en Paraguay, Bolivia y LATAM.',
      encajaFeatureInventory: 'Inventario',
      encajaFeatureClients: 'Clientes',
      encajaFeatureSales: 'Ventas',
      encajaFeatureBilling: 'Facturación',
      encajaPrimaryCta: 'Probar 14 días gratis →',
      encajaSecondaryCta: 'Ver planes y precios',
      encajaPlanPopularBadge: 'Más popular',
      encajaMockLabel: 'Panel en vivo',
      encajaMockMetricOrders: 'Pedidos hoy',
      encajaMockMetricOrdersValue: '24',
      encajaMockMetricStock: 'Alertas de stock',
      encajaMockMetricStockValue: '3',
      encajaMockMetricRevenue: 'Ventas del mes',
      encajaMockMetricRevenueValue: '₲ 4.2M',
      heroBadge: '✨ Servicios digitales para LATAM',
      heroTitle: 'Tu presencia web,',
      heroTitleHighlight: 'hecha para vender.',
      heroTitleSuffix: '',
      heroSubtitle:
        'Diseñamos páginas web, software a medida y automatizaciones que convierten visitas en clientes. Rápido, claro y pensado para tu negocio en Latinoamérica.',
      heroTitleDomain: 'Registra tu dominio y',
      heroTitleDomainHighlight: 'lanza tu marca',
      heroSubtitleDomain: 'Te ayudamos a asegurar dominios, configurar DNS y conectar tu presencia digital sin fricción.',
      heroSubtitleSupportDomain: 'Ideal para negocios que quieren empezar rápido con una base técnica sólida.',
      heroTitleSoftware: 'Software a medida para',
      heroTitleSoftwareHighlight: 'operar y escalar',
      heroSubtitleSoftware: 'Diseñamos sistemas personalizados para ventas, operaciones y automatización comercial.',
      heroSubtitleSupportSoftware: 'Desde flujo inicial hasta roadmap por fases con KPIs claros.',
      heroPrimaryCta: 'Quiero empezar ahora →',
      heroSecondaryCta: 'Hablar por WhatsApp',
      heroProofOne: '⚡ Respuesta rápida',
      heroProofTwo: '🌐 Clientes en toda LATAM',
      heroProofThree: '🛡️ Pagos en cripto disponibles',
      statDeliveredValue: '+50',
      statDeliveredLabel: 'Proyectos entregados',
      statReviewsValue: '9★',
      statReviewsLabel: 'Reseñas promedio',
      statResponseValue: '24h',
      statResponseLabel: 'Tiempo de respuesta',
      statCoverageValue: 'LATAM',
      statCoverageLabel: 'Cobertura regional',
      heroMockBrowserUrl: 'tu-marca.com',
      heroMockMetricVisits: 'VISITAS',
      heroMockMetricVisitsValue: '12.4k',
      heroMockMetricClients: 'CLIENTES',
      heroMockMetricClientsValue: '+38%',
      heroMockMetricSpeed: 'VELOCIDAD',
      heroMockMetricSpeedValue: '98',
      heroMockLiveStatus: 'En vivo · Conversiones hoy',
      fabWhatsAppAria: 'Abrir WhatsApp',
      faqBadge: 'Preguntas frecuentes',
      faqHeadingLead: 'Resolvemos tus',
      faqHeadingAccent: 'dudas',
      faqQ1: '¿Cuánto tarda mi página web?',
      faqA1:
        'Una landing o web simple puede estar en línea en días. Los proyectos a medida dependen del alcance: te damos calendario y entregas por etapa en la propuesta.',
      faqQ2: '¿Cómo son los pagos?',
      faqA2:
        'Puedes pagar con tarjeta, transferencia o criptomonedas. Acordamos precio y hitos por escrito para que no haya sorpresas.',
      faqQ3: '¿Atienden a clientes de toda Latinoamérica?',
      faqA3:
        'Sí. Trabajamos con equipos en toda la región y hablamos español e inglés según tu preferencia.',
      faqQ4: '¿Qué pasa después del lanzamiento?',
      faqA4:
        'Te capacitamos, damos soporte por WhatsApp y podemos seguir con mejoras y nuevas integraciones según tu plan.',
      faqQ5: '¿Puedo migrar desde otro proveedor?',
      faqA5:
        'Sí. Planificamos la migración de dominio, hosting y contenido para minimizar cortes y cuidar SEO y datos.',
      processBadge: 'Cómo trabajamos',
      processHeadingLead: 'Un proceso',
      processHeadingAccent: 'simple y',
      processHeadingTrail: 'transparente',
      processSubtitle:
        'Sin complicaciones. Avanzamos por etapas cortas, comunicación directa por WhatsApp y entregas verificables.',
      processStep1Title: 'Conversamos tu proyecto',
      processStep1Desc:
        'Entendemos tu negocio, tus clientes y a dónde quieres llegar. Sin tecnicismos.',
      processStep2Title: 'Propuesta clara',
      processStep2Desc: 'Alcance, tiempos y precio fijo. Sabes exactamente qué recibes y cuándo.',
      processStep3Title: 'Diseño y desarrollo',
      processStep3Desc:
        'UX/UI, SEO y código limpio. Te mostramos avances para que valides en cada paso.',
      processStep4Title: 'Lanzamiento y soporte',
      processStep4Desc:
        'Publicamos, capacitamos a tu equipo y seguimos contigo después del go-live.',
      whyChooseBadge: 'Por qué elegirnos',
      whyChooseLine1a: 'La',
      whyChooseLine1b: 'mejor opción',
      whyChooseLine2a: 'para tu',
      whyChooseLine2b: 'negocio',
      whyChoose1Title: 'Entregas rápidas',
      whyChoose1Desc: 'Tu sitio en línea en días, no en meses.',
      whyChoose2Title: 'Pensado para vender',
      whyChoose2Desc: 'UX, SEO y copy orientados a conversión.',
      whyChoose3Title: 'Soporte cercano',
      whyChoose3Desc: 'Hablas con humanos por WhatsApp, no con bots.',
      whyChoose4Title: 'Pagos flexibles',
      whyChoose4Desc: 'Tarjeta, transferencia o cripto. Tú eliges.',
      whyChoose5Title: 'Precio claro',
      whyChoose5Desc: 'Sin sorpresas. Lo que ves es lo que pagas.',
      whyChoose6Title: 'Toda LATAM',
      whyChoose6Desc: 'Atendemos en español e inglés en toda la región.',
      clientsBadge: 'Clientes',
      clientsHeadingLead: 'Lo que dicen',
      clientsHeadingAccent: 'de nuestro trabajo',
      testimonial1Quote:
        'En menos de dos semanas teníamos la tienda en línea y vendiendo. El soporte por WhatsApp es buenísimo.',
      testimonial1Name: 'Carolina M.',
      testimonial1Meta: 'Boutique online · Bolivia',
      testimonial1Initial: 'C',
      testimonial2Quote:
        'Profesionales y muy claros con el alcance. El sitio carga rapidísimo y nos llegan más consultas.',
      testimonial2Name: 'Diego R.',
      testimonial2Meta: 'Estudio jurídico · Perú',
      testimonial2Initial: 'D',
      testimonial3Quote:
        'Me encanta cómo quedó. El diseño se ve premium y por fin tengo embudo de captación que funciona.',
      testimonial3Name: 'Valentina S.',
      testimonial3Meta: 'Coach de negocios · Colombia',
      testimonial3Initial: 'V',
      featureRealTitles: 'Contenido que posiciona',
      featureAIPowered: 'Catálogo en expansión',
      featureFreeForever: 'Soporte regional',
      ctaButton: 'Ir a cuenta e inicio de sesión',
      trustText: 'Consulta noticias de tecnologia y negocios web sin crear cuenta.',
      trustDomainOne: 'Precio final visible antes de pagar',
      trustDomainTwo: 'Acompañamiento DNS y activación',
      trustDomainThree: 'Soporte humano para registro',
      trustOffersOne: 'Alcance y entregables definidos',
      trustOffersTwo: 'Roadmap claro por etapas',
      trustOffersThree: 'Soporte post-entrega disponible',
      trustContactOne: 'Atención regional LATAM',
      trustContactTwo: 'Respuesta comercial rápida',
      trustContactThree: 'Sin compromiso inicial',
      trustFinalOne: 'Primera respuesta en menos de 24h',
      trustFinalTwo: 'Cobertura para Paraguay, Bolivia y LATAM',
      trustFinalThree: 'Pago flexible: tradicional y cripto',
      heroCardBlog: 'Noticias',
      heroCardStore: 'Tienda',
      heroCardLatam: 'LatAm',
      servicesTitle: 'Qué ofrecemos',
      servicesBadge: 'Qué ofrecemos',
      servicesHeadingLine1: 'Servicios digitales',
      servicesHeadingAccent: 'de punta a punta',
      servicesSubtitle:
        'Para pymes, profesionales y equipos comerciales. Priorizamos velocidad, confianza y una propuesta clara para cerrar más clientes.',
      servicesMoreTitle: 'Más soluciones digitales',
      productCardCta: 'Ver detalle',
      productCheckoutCta: 'Pagar ahora',
      productViewDetailCta: 'Ver más',
      productPriceOnRequest: 'Consultar precio',
      productOfferBadge: 'Oferta -{{percent}}%',
      productPriceMonthlySuffix: ' /mes',
      offerViewDetailCta: 'Ver más',
      productDomainsTitle: 'Registro de dominios',
      productDomainsDesc: 'Reserva y renovación con asistencia de DNS y buenas prácticas para tu marca.',
      productHostingTitle: 'Hosting administrado (Hestia CP)',
      productHostingDesc: 'Planes Cobre, Plata y Oro con panel Hestia (más liviano que cPanel), soporte en español y curso gratuito para clientes.',
      productVpsTitle: 'Servidor VPS + Hestia CP',
      productVpsDesc: 'VPS administrado con Hestia preinstalado, soporte técnico y curso gratuito de gestión para tu equipo.',
      productWebSimpleTitle: 'Páginas web simples',
      productWebSimpleDesc: 'Landings y sitios listos para publicar rápido, con alcance fijo y precio claro.',
      productWebCustomTitle: 'Páginas web a medida',
      productWebCustomDesc: 'UX/UI, SEO y arquitectura pensados para convertir visitas en clientes.',
      productSoftwareCustomTitle: 'Software & automatizaciones',
      productSoftwareCustomDesc: 'Integraciones, dashboards y bots que ahorran horas a tu equipo comercial.',
      productAutomationTitle: 'Automatización de procesos',
      productAutomationDesc: 'Automatizamos tareas repetitivas de ventas y operación para reducir tiempos y errores.',
      productDashboardsTitle: 'Dashboards operativos',
      productDashboardsDesc: 'Paneles con KPIs de negocio para seguimiento comercial, operaciones y toma de decisiones.',
      productIntegrationsTitle: 'Integraciones de negocio',
      productIntegrationsDesc: 'Conectamos pagos, CRM, ERP y mensajería para que tus datos y procesos fluyan en un solo sistema.',
      productSupportTitle: 'Soporte y SLA',
      productSupportDesc: 'Planes de soporte continuo con tiempos de respuesta acordados y mejora evolutiva.',
      regionNote: 'Presencia comercial en Paraguay y Bolivia; proyectos en toda Latinoamérica.',
      contactSectionTitle: 'Habla con nosotros por WhatsApp',
      contactParaguayLine: 'Paraguay',
      contactBoliviaLine: 'Bolivia',
      leadFormTitle: 'Diagnóstico rápido (paso 1 mínimo)',
      leadNamePlaceholder: 'Tu nombre',
      leadNeedWebsite: 'Quiero una web comercial',
      leadNeedDomain: 'Quiero registrar dominio',
      leadNeedSoftware: 'Quiero software a medida',
      leadStep1Cta: 'Continuar',
      leadEmailPlaceholder: 'Email (opcional)',
      leadCompanyPlaceholder: 'Empresa (opcional)',
      leadNotesPlaceholder: 'Contexto breve (opcional)',
      leadBack: 'Volver',
      leadSubmitCta: 'Enviar por WhatsApp',
      leadFormErrorNameRequired: 'Escribe tu nombre para continuar.',
      leadWhatsappIntro: 'Hola, quiero un diagnóstico comercial.',
      leadFieldName: 'Nombre',
      leadFieldNeed: 'Necesidad',
      leadFieldEmail: 'Email',
      leadFieldCompany: 'Empresa',
      leadFieldNotes: 'Notas',
      contactEnglishLine: 'Atención en inglés por mensaje.',
      contactEmailLine: 'Email:',
      contactParaguayDisplay: '0983 062031',
      contactBoliviaDisplay: '77499919',
      contactWhatsAppCta: 'Escribir por WhatsApp',
      cryptoSectionTitle: 'Aceptamos criptomonedas',
      cryptoSectionBody:
        'Compra más rápido, confirma antes y avanza tu proyecto sin fricción. Ideal para clientes en toda LATAM.',
      cryptoSectionHighlight: 'Pago ágil para dominios, servicios web y software a medida.',
      cryptoSectionCta: 'Quiero comprar con criptos',
      latamClientsLine: 'Recibimos pedidos y trabajamos con clientes de toda LATAM.',
      noCommitmentLine: 'Pregúntanos sin compromiso: somos tu mejor opción.',
      offersTitle: 'Paquetes de mayor conversión',
      offersSubtitle: 'Inspirados en líderes del mercado: planes simples, propuesta clara y CTA directo para decidir rápido.',
      offersDiscountTitle: 'Ofertas con descuento',
      offersDiscountLead: 'Precio de referencia tachado — aprovecha el descuento activo.',
      offerStarterBadge: 'Entrada rápida',
      offerStarterTitle: 'Landing simple 5 secciones',
      offerStarterDesc: 'Landing con 5 bloques estándar, dominio y hosting (1 año) y 5 correos corporativos. Lista para captar clientes.',
      offerStarterPriceHint: 'USD 299 · paquete fast-lane',
      offerStarterCta: 'Pagar ahora',
      offerStarterBestFor: 'Negocios que necesitan presencia digital rápida',
      offerStarterUpgradeTo: 'Growth',
      offerBusinessBadge: 'Más elegido',
      offerBusinessTitle: 'Web de negocio + automatización',
      offerBusinessDesc: 'Sitio multipágina con secciones de servicios, integración de WhatsApp y embudo de captación.',
      offerBusinessPriceHint: 'Ideal para escalar ventas',
      offerBusinessCta: 'Reservar diagnóstico',
      offerBusinessBestFor: 'Equipos con proceso comercial activo',
      offerBusinessUpgradeTo: 'Scale',
      offerScaleBadge: 'Escala',
      offerScaleTitle: 'Scale: operación y embudo avanzado',
      offerScaleDesc: 'Optimización de conversión, automatización comercial y mejoras continuas por iteraciones.',
      offerScalePriceHint: 'Para crecimiento sostenido por datos',
      offerScaleCta: 'Quiero escalar',
      offerScaleBestFor: 'Negocios en expansión con metas trimestrales',
      offerScaleUpgradeTo: 'Custom',
      offerCustomBadge: 'A medida',
      offerCustomTitle: 'Sistema personalizado',
      offerCustomDesc: 'Diseño y desarrollo de plataforma propia: panel, roles, procesos y módulos adaptados a tu operación.',
      offerCustomPriceHint: 'Roadmap por fases + soporte',
      offerCustomCta: 'Solicitar propuesta',
      offerCustomBestFor: 'Operaciones con requisitos específicos y alta complejidad',
      offerCustomUpgradeTo: 'Plan evolutivo por fases',
      offerBestForLabel: 'Ideal para',
      offerUpgradeLabel: 'Siguiente upgrade',
      proofTitle: 'Prueba real de resultados',
      proofSubtitle: 'Datos, señales de confianza y evidencia de ejecución para tomar una decisión con menor riesgo.',
      proofMetricOneValue: '+34%',
      proofMetricOneLabel: 'Promedio de mejora en tasa de contacto inicial',
      proofMetricTwoValue: '-27%',
      proofMetricTwoLabel: 'Reducción de tiempo de respuesta comercial',
      proofMetricThreeValue: '4.8/5',
      proofMetricThreeLabel: 'Satisfacción reportada en acompañamiento del proyecto',
      proofTestimonialOneQuote: '"En pocas semanas pasamos de mensajes sueltos a un flujo comercial claro con web + WhatsApp."',
      proofTestimonialOneAuthor: 'Equipo comercial · Servicios B2B (PY)',
      proofTestimonialTwoQuote: '"Nos ayudaron a ordenar oferta, pricing y seguimiento; ahora cerramos con menos idas y vueltas."',
      proofTestimonialTwoAuthor: 'Dirección · Negocio digital (BO)',
      proofAuthorityOne: 'Metodología orientada a conversión',
      proofAuthorityTwo: 'Arquitectura técnica escalable',
      proofAuthorityThree: 'Implementación por fases con KPIs',
      proofAuthorityFour: 'Soporte LATAM y mejora continua',
      featuresTitle: 'Por qué trabajar con nosotros',
      featuresSubtitle: 'Combinamos contenido, SEO y servicios para que tu marca tenga base técnica sólida.',
      featurePersonalizedTitle: 'Noticias de tecnologia y negocios web',
      featurePersonalizedDesc: 'Contenido útil sobre dominios, hosting, seguridad y crecimiento digital para empresas.',
      featureRateTitle: 'Tienda de servicios',
      featureRateDesc: 'Dominios, hosting, VPS y desarrollo web en un mismo ecosistema (e-commerce en despliegue).',
      featureTrackTitle: 'SEO y analítica',
      featureTrackDesc: 'Metadatos, URLs limpias y estructura pensada para buscadores y redes.',
      featureSearchTitle: 'Alcance regional',
      featureSearchDesc: 'Enfoque LatAm: idioma, contexto local y canales de contacto en la región.',
      featureControlTitle: 'Proyectos claros',
      featureControlDesc: 'Alcances definidos para sitios simples y hojas de ruta para proyectos a medida.',
      featureOwnAITitle: 'Acompañamiento continuo',
      featureOwnAIDesc: 'Te acompañamos con mejoras, soporte y evolución según crece tu negocio.',
      howItWorksTitle: 'Cómo empezar',
      howItWorksSubtitle: 'Tres pasos para conocernos',
      step1Title: 'Lee y explora',
      step1Desc: 'Explora noticias de tecnología y servicios para identificar oportunidades reales para tu negocio.',
      step2Title: 'Elige tu servicio',
      step2Desc: 'Dominio, web o software a medida: te guiamos según tu etapa y objetivo comercial.',
      step3Title: 'Hablemos',
      step3Desc: 'Si no tienes claro lo que buscas, empieza aquí: te ayudamos a definir la mejor opción para avanzar.',
      step3Cta: 'Empieza aquí',
      ctaTitle: '¿Listo para vender más?',
      ctaSubtitle: 'Crea tu cuenta para coordinar tu proyecto web o sistema a medida. También puedes continuar con Google.',
      finalCtaLine1: '¿Listo para llevar tu negocio',
      finalCtaLine2: 'al siguiente nivel?',
      finalCtaSubtitle:
        'Cuéntanos tu idea por WhatsApp. En menos de 24 horas tendrás una propuesta clara con tiempos y precio.',
      finalCtaPrimary: 'Hablar por WhatsApp →',
      finalCtaSecondary: 'Escríbenos por correo',
      mobileStickyPrimaryCta: 'WhatsApp',
      mobileStickySecondaryCta: 'Correo',
      footerWordEstu: 'Es tu',
      footerWordMejor: 'mejor',
      footerWordOpcion: 'opción',
      footerBrandFull: 'Es tu mejor opción',
      footerCopyrightLine: '© 2026 Es tu mejor opción · Hecho con cariño en LATAM',
      footerEncajaPlans: 'Planes Encaja',
      footerEncajaPanel: 'Panel Encaja',
      footerText: 'Es tu mejor opción · Noticias y servicios digitales para Latinoamérica',
      footerCopyright: '© 2026 Es tu mejor opción',
      latestNewsTitle: 'Últimas noticias',
      latestNewsReadMore: 'Leer más',
      latestNewsNone: 'No hay noticias recientes.',
      allNewsLink: 'Aquí todas las noticias',
      domainSearchTitle: 'Busca tu dominio',
      domainSearchSubtitle:
        'Escribe tu marca o un nombre (sin extensión o con una). Te mostramos varias extensiones con disponibilidad y una referencia de precio de registro.',
      domainSearchTrustLine: 'Activación rápida + soporte DNS para acompañarte en todo el proceso.',
      domainSearchPlaceholder: 'ejemplo.com o mi-marca',
      domainSearchButton: 'Buscar',
      domainSearchLoading: 'Consultando…',
      domainSearchErrorRequired: 'Escribe un nombre de dominio.',
      domainSearchErrorUnconfigured:
        'La búsqueda de dominios no está disponible por ahora. Vuelve más tarde o contáctanos.',
      domainSearchErrorFailed: 'No se pudo consultar el dominio. Intenta de nuevo.',
      domainSearchErrorInvalid: 'Nombre no válido. Usa letras, números y guiones (una etiqueta, ej. mi-marca).',
      domainSuggestionsTitle: 'Opciones por extensión',
      domainSuggestionsFor: 'Para el nombre',
      domainSuggestionsHeadDomain: 'Dominio',
      domainSuggestionsHeadStatus: 'Estado',
      domainSuggestionsHeadPrice: 'Precio de venta',
      domainSuggestionsHeadAction: 'Acción',
      domainSuggestionsFootnote:
        'Precio final de venta por dominio y periodo de registro.',
      domainNextStepNote:
        'Puedes elegir una fila y continuar en tu cuenta para coordinar el registro, DNS y el resto del proyecto.',
      domainContinueCta: 'Continuar al pago',
      domainSelectFirst: 'Elige un dominio en la tabla',
      domainRowBuyNow: 'Comprar',
      domainUnavailableBadge: 'No disponible',
      domainRowSelectLabel: 'Seleccionar {domain}',
      domainPremiumBadge: 'Premium',
      domainSpecialOfferBadge: 'Oferta especial',
      domainPriceQuote: '{amount} {currency} / {months} meses',
      domainPriceQuoteYear: '{amount} {currency} por año',
      domainPriceSimple: '{amount}',
      domainStatusAvailable: 'Disponible para registro',
      domainStatusPrereg: 'Pre-registro o fase especial',
      domainStatusReserved: 'Reserva liberable (sujeto a condiciones)',
      domainStatusTaken: 'No disponible',
      domainStatusError: 'No se pudo verificar',
      domainStatusSuspended: 'Registro suspendido',
      domainStatusBlocked: 'No permitido (lista de bloqueo)',
      domainStatusUnknown: 'Estado desconocido',
      domainPremiumHint: 'Dominio premium: el precio final puede variar.',
      // Auth errors
      errorGoogleNotConfigured: 'Google OAuth no está configurado en el servidor.',
      errorGoogleAuthFailed: 'Error al autenticar con Google. Intenta de nuevo.',
      errorNoUser: 'No se pudo crear o obtener el usuario.',
      errorCallback: 'Error en el proceso de autenticación.',
      errorSession: 'Error al guardar la sesión.',
      errorSignIn: 'Error al iniciar sesión con Google.',
      errorSignInGeneric: 'Error al iniciar sesión.',
      errorConnection: 'Error de conexión.'
    },
    // Página Todas las noticias
    allNews: {
      title: 'Todas las noticias',
      backToHome: 'Volver al inicio',
      loading: 'Cargando noticias...',
      noPosts: 'No hay noticias publicadas.',
      readMore: 'Leer más'
    },
    conversionProof: {
      title: 'Prueba de conversión',
      pointOne: 'Casos con mejoras medibles en tasa de contacto y respuesta comercial.',
      pointTwo: 'Implementación orientada a entregables y prioridades de negocio.',
      pointThree: 'Seguimiento posterior para optimización continua.',
      badgeOne: 'Resultados medibles',
      badgeTwo: 'Soporte LATAM',
      cta: 'Ver más evidencia'
    },
    productPage: {
      brandSuffix: 'Es tu mejor opción',
      errorLoad: 'No se pudo cargar la página del servicio.',
      notFound: 'No encontramos este servicio o paquete.',
      backHome: 'Volver al inicio',
      categoryLabel: 'Categoría',
      noCategory: 'Sin categoría',
      defaultDescription: 'Servicio digital orientado a resultados comerciales y crecimiento sostenible.',
      summaryTitle: 'Resumen del paquete',
      includesTitle: 'Tu landing incluye 5 secciones',
      infrastructureTitle: 'Infraestructura incluida',
      conditionsTitle: 'Condiciones claras',
      problemTitle: 'Problema que resolvemos',
      problemBody: 'Lanzamos tu presencia online sin fricción: una landing que convierte y todo lo necesario para publicarla.',
      processTitle: 'Cómo funciona después del pago',
      processOne: 'Confirmamos tu pedido en la cuenta y te pedimos marca, textos y referencias.',
      processTwo: 'Diseñamos e implementamos la landing con las 5 secciones acordadas.',
      processThree: 'Activamos dominio, hosting y correos; te entregamos accesos y checklist.',
      deliverablesTitle: 'Qué recibes',
      deliverableOne: 'Landing publicada con diseño responsive y CTA de contacto.',
      deliverableTwo: 'Dominio y hosting activos el primer año (según proveedor).',
      deliverableThree: '5 cuentas de correo corporativo configuradas en tu dominio.',
      timelineTitle: 'Plazo estimado',
      timelineBody: 'Entrega típica en pocos días hábiles tras recibir tu contenido.',
      priceLabel: 'Precio del paquete',
      priceNote: 'Pago único en USD. Renovaciones de dominio, hosting y correo se cotizan aparte después del primer año.',
      payNowCta: 'Pagar ahora',
      whatsappCta: 'Contáctanos',
      whatsappPrefill: 'Hola, tengo una consulta sobre el paquete: {productName}',
      noPaymentMethods: 'El pago en línea no está disponible ahora. Escríbenos por WhatsApp.',
      checkoutNotEnabled: 'Este producto no tiene pago en línea habilitado. Escríbenos por WhatsApp para contratarlo.',
      loginHint: 'Inicia sesión para pagar y guardar tu compra en tu cuenta.',
      starterSection1: 'Hero con propuesta de valor y botón de contacto',
      starterSection2: 'Servicios: qué ofreces y a quién',
      starterSection3: 'Beneficios y diferenciadores',
      starterSection4: 'Prueba social (testimonios, logos o casos)',
      starterSection5: 'Contacto con formulario y CTA final',
      starterInfra1: 'Registro de dominio (1 año)',
      starterInfra2: 'Hosting compartido (1 año)',
      starterInfra3: '5 cuentas de correo corporativo en tu dominio',
      starterCondition1: 'Precio fijo del paquete publicado en esta página',
      starterCondition2: 'Contenidos (textos, logos) los envías tú o los redactamos como add-on',
      starterCondition3: 'Renovaciones posteriores se cotizan por separado',
      starterCondition4: 'Tras el pago te contactamos para activar el proyecto',
      genericInclude1: 'Implementación alineada al objetivo del paquete',
      genericInclude2: 'Entregables documentados y soporte de puesta en marcha',
      genericInclude3: 'Ajustes acordados dentro del alcance publicado',
      relatedTitle: 'Servicios relacionados',
      relatedCta: 'Ver detalle',
      breadcrumbHome: 'Inicio',
      breadcrumbServices: 'Servicios',
      starterTagline: 'Todo lo necesario para publicar tu negocio online: landing de 5 secciones, dominio, hosting y correos corporativos.',
      trustBadge1: 'Entrega en pocos días hábiles',
      trustBadge2: 'Soporte LATAM',
      trustBadge3: 'Precio fijo publicado',
      detailsTitle: 'Detalles del paquete',
      galleryAltHero: 'Vista hero de la landing',
      galleryAltSections: 'Secciones de servicios y beneficios',
      galleryAltMobile: 'Vista responsive en móvil',
      galleryAltInfra: 'Dominio, hosting y correos',
      galleryAltFull: 'Vista completa de la página',
      galleryAltDefault: 'Vista del servicio',
      encajaMonthlyPriceNote: 'Suscripción mensual en USD. La prueba de 14 días se activa en Encaja; la contratación del plan se gestiona con Estumejoropcion.',
      encajaTrialCta: 'Probar 14 días gratis en Encaja',
      encajaContractCta: 'Contratar plan',
      encajaSalesCta: 'Solicitar propuesta',
      encajaLoginHint: 'La prueba gratuita se inicia en encaja.estumejoropcion.com. Para activar tu plan de pago, escríbenos por WhatsApp.',
      encajaCondition1: 'Precio mensual publicado en esta página',
      encajaCondition2: 'Prueba gratuita de 14 días en el panel Encaja',
      encajaCondition3: 'Facturación y soporte comercial vía Estumejoropcion',
      encajaStarterFeature1: 'Catálogo de productos y control de stock',
      encajaStarterFeature2: 'Movimientos de inventario y un depósito',
      encajaStarterFeature3: 'Hasta 2 usuarios en el panel',
      encajaBusinessFeature1: 'Todo Starter + módulo CRM',
      encajaBusinessFeature2: 'Clientes, contactos y seguimiento comercial',
      encajaBusinessFeature3: 'Hasta 5 usuarios',
      encajaProfessionalFeature1: 'Inventario, CRM y facturación integrados',
      encajaProfessionalFeature2: 'Multi-depósito y operación comercial completa',
      encajaProfessionalFeature3: 'Hasta 15 usuarios — plan recomendado',
      encajaEnterpriseFeature1: 'Límites y módulos a medida',
      encajaEnterpriseFeature2: 'SLA y acompañamiento dedicado',
      encajaEnterpriseFeature3: 'Integraciones según discovery',
      encajaEnterpriseCondition1: 'Alcance y precio según propuesta comercial',
      encajaEnterpriseCondition2: 'Onboarding guiado con Estumejoropcion',
      encajaEnterpriseCondition3: 'Soporte prioritario acordado por contrato'
    },
    checkoutProduct: {
      title: 'Pago del paquete',
      intro:
        'El precio y el alcance son los publicados en la ficha del producto. Tras confirmar el pago, activamos tu proyecto desde tu cuenta.',
      backHome: 'Volver al inicio',
      backProduct: 'Volver a la ficha',
      missingSlug: 'Falta el producto en la URL. Vuelve al inicio y elige un paquete.',
      loginRequiredTitle: 'Inicia sesión para pagar',
      loginRequiredBody: 'Necesitas una cuenta para completar el pago y ver tu compra.',
      goToLogin: 'Ir a iniciar sesión',
      loadingQuote: 'Cargando precio…',
      labelProduct: 'Producto',
      labelAmount: 'Total',
      amountUsd: 'USD {amount}',
      quoteDisclaimer: 'Este es el precio publicado para este paquete. Sin sorpresas en el checkout.',
      payWithPayPal: 'Pagar con PayPal',
      payWithBinanceWallet: 'Pagar con depósito USDT (Binance)',
      noGateways: 'Los métodos de pago no están configurados en el servidor.',
      payPalCancelled: 'Cancelaste el pago en PayPal. Puedes intentar de nuevo.',
      binanceInstructionsTitle: 'Envía USDT a esta dirección de depósito',
      binanceScanQr: 'Escanea el código QR con Binance u otra wallet compatible',
      binanceDownloadQr: 'Descargar QR',
      binanceQrHint: 'También puedes guardar la imagen y escanearla desde la galería de tu teléfono.',
      binanceReference: 'Referencia interna:',
      binanceNetwork: 'Red:',
      binanceAmount: 'Monto exacto (USDT):',
      binanceAmountHint:
        'Equivale aproximado a tu compra en USD ({usd}). Debe coincidir con el monto mostrado para asociar el pago.',
      binanceAddress: 'Dirección:',
      binanceTag: 'Memo / Tag:',
      binancePolling: 'Detectamos el depósito automáticamente. Esta página se actualiza cuando llegue.',
      successTitle: 'Pago confirmado',
      successBody: 'Hemos registrado tu compra de {productName}.',
      fulfillmentNote:
        'Te contactamos en breve para solicitar marca, textos y datos de dominio. Puedes ver el pedido en tu cuenta.',
      goToAccount: 'Ir a mi cuenta',
      errorQuote: 'No se pudo cargar el producto. Intenta de nuevo.',
      errorProductNotFound: 'Este producto no está disponible.',
      errorPriceUnavailable: 'No hay precio disponible para este producto.',
      errorPayPalStart: 'No se pudo iniciar PayPal.',
      errorPayPalCapture: 'No se pudo confirmar el pago en PayPal.',
      errorBinanceStart: 'No se pudo preparar la dirección de depósito.'
    },
    // Tutorial de Groq
    groqTutorial: {
      back: 'Volver',
      title: 'Cómo Configurar Groq API para Recomendaciones Reales',
      subtitle: 'Aprende cómo obtener tu clave API gratuita de Groq en menos de 5 minutos',
      whyNeededTitle: '¿Por qué necesitas Groq?',
      whyNeeded: 'Para generar recomendaciones reales y personalizadas, necesitamos acceso a modelos de IA avanzados. Groq proporciona este servicio de forma rápida y económica.',
      freeTitle: 'Plan Gratuito Disponible',
      freeDescription: 'Groq ofrece un plan gratuito generoso con límites diarios. Para uso personal, es más que suficiente y no requiere tarjeta de crédito.',
      step1Title: 'Crear una cuenta en Groq',
      step1Description: 'Visita la consola de Groq y crea una cuenta gratuita. Puedes usar tu cuenta de Google para iniciar sesión rápidamente.',
      openConsole: 'Abrir Consola de Groq',
      step1Note: 'Nota: El registro es completamente gratuito y no requiere tarjeta de crédito.',
      step2Title: 'Generar tu API Key',
      step2Description: 'Una vez dentro de la consola, ve a la sección de API Keys y crea una nueva clave. Cópiala inmediatamente porque solo se mostrará una vez.',
      step2Code: 'gsk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      copy: 'Copiar',
      copied: '¡Copiado!',
      step3Title: 'Configurar en Es tu mejor opción',
      step3Description: 'Ahora que tienes tu API Key, sigue estos pasos para configurarla:',
      step3Item1: 'Ve a la sección de Configuración en tu dashboard',
      step3Item2: 'Pega tu API Key de Groq en el campo correspondiente',
      step3Item3: 'Guarda los cambios y ¡listo! Ya puedes generar recomendaciones reales',
      goToSettings: 'Ir a Configuración',
      faqTitle: 'Preguntas Frecuentes',
      faq1Question: '¿Es realmente gratis?',
      faq1Answer: 'Sí, Groq ofrece un plan gratuito con límites generosos. Para uso personal de recomendaciones, es más que suficiente. Solo pagarías si excedes los límites del plan gratuito.',
      faq2Question: '¿Necesito tarjeta de crédito?',
      faq2Answer: 'No, el plan gratuito de Groq no requiere tarjeta de crédito. Puedes empezar a usarlo inmediatamente después de crear tu cuenta.',
      faq3Question: '¿Es seguro compartir mi API Key?',
      faq3Answer: 'Tu API Key se almacena de forma segura en tu cuenta y solo tú puedes verla. Nunca la compartas públicamente. En Es tu mejor opción, tu clave se guarda de forma encriptada y solo se usa para generar borradores del blog.',
      faq4Question: '¿Qué pasa si excedo los límites gratuitos?',
      faq4Answer: 'Groq tiene límites muy generosos. Si los excedes, puedes configurar límites de gasto en tu cuenta de Groq o considerar actualizar a un plan de pago. Los precios son muy económicos (desde $0.05 por millón de tokens).',
      readyTitle: '¿Listo para empezar?',
      readyDescription: 'Configura tu API Key de Groq ahora y comienza a recibir recomendaciones reales y personalizadas.',
      configureNow: 'Configurar Ahora',
      learnMore: 'Aprende más sobre Groq',
      linkText: 'Ver tutorial completo'
    },
    // Mensajes generales
    common: {
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      save: 'Guardar',
      delete: 'Eliminar',
      edit: 'Editar',
      close: 'Cerrar',
      search: 'Buscar',
      filter: 'Filtrar',
      noResults: 'No se encontraron resultados',
      noData: 'No hay datos disponibles',
      note: 'Nota',
      logout: 'Cerrar sesión',
      aiServiceNotAvailable: 'El servicio de IA no está disponible. No se podrán generar nuevas recomendaciones automáticas.',
      learnHowToConfigure: 'Aprende cómo configurar Groq API para recomendaciones reales →',
      errorLoadingData: 'Error cargando los datos.',
      aiAvailable: 'IA disponible',
      aiNotAvailable: 'IA no disponible',
      errorGeneratingRecommendations: 'Error generando recomendaciones',
      generatingRecommendations: 'Generando recomendaciones...',
      errorMarkingWatched: 'Error marcando el contenido como visto.',
      errorUpdatingPreferences: 'Error actualizando las preferencias.',
      errorLoadingMovies: 'Error cargando las películas',
      errorLoadingSeries: 'Error cargando las series',
      errorLoadingMusic: 'Error cargando la música',
      errorMarkingListened: 'Error marcando como escuchada',
      checkingAuth: 'Verificando sesión...',
      // Blog
      blog: {
        loading: 'Cargando...',
        error: 'Error',
        postNotFound: 'Artículo no encontrado',
        errorLoading: 'Error cargando el artículo',
        backToHome: 'Volver al inicio',
        views: 'vistas',
        rateThisContent: 'Calificar este contenido',
        rateBusiness: 'Calificar este contenido de business',
        rateTech: 'Calificar este contenido tech',
        rateNews: 'Calificar esta noticia',
        goToRate: 'Ir a calificar',
        adminTitle: 'Gestión de contenido',
        newPost: 'Nuevo artículo',
        editPost: 'Editar artículo',
        title: 'Título',
        category: 'Tipo de contenido',
        status: 'Estado',
        publishedAt: 'Fecha de publicación',
        actions: 'Acciones',
        noPosts: 'No hay artículos aún',
        published: 'Publicado',
        draft: 'Borrador',
        viewPost: 'Ver artículo',
        edit: 'Editar',
        delete: 'Eliminar',
        confirmDelete: '¿Estás seguro de que quieres eliminar este artículo?',
        errorDeleting: 'Error al eliminar el artículo',
        errorSaving: 'Error al guardar el artículo',
        saving: 'Guardando...',
        save: 'Guardar',
        excerpt: 'Resumen',
        excerptPlaceholder: 'Resumen corto del artículo (opcional)',
        excerptHelp: 'Este texto aparecerá en las vistas previas y en los meta tags',
        content: 'Contenido',
        contentPlaceholder: 'Escribe el contenido del artículo en HTML...',
        contentHelp: 'Puedes usar HTML para formatear el contenido',
        featuredImage: 'Imagen destacada',
        imageUrlPlaceholder: 'URL de la imagen destacada',
        uploadImage: 'Subir imagen',
        uploadingImage: 'Subiendo…',
        uploadImageHelp: 'Sube una imagen o pega una URL',
        seoSettings: 'Configuración SEO',
        metaTitle: 'Meta Título',
        metaTitlePlaceholder: 'Título para SEO (opcional, por defecto usa el título del artículo)',
        metaTitleHelp: 'Si se deja vacío, se usará el título del artículo',
        metaDescription: 'Meta descripción',
        metaDescriptionPlaceholder: 'Descripción para SEO (opcional)',
        metaDescriptionHelp: 'Descripción que aparecerá en los resultados de búsqueda',
        metaKeywords: 'Meta Keywords',
        metaKeywordsPlaceholder: 'Palabras clave separadas por comas',
        metaKeywordsHelp: 'Palabras clave para SEO (opcional)',
        titlePlaceholder: 'Título del artículo',
        adminAccessDenied: 'Acceso denegado. Se requiere rol de superusuario.',
        accessDeniedOops: '¡Uy!',
        accessDeniedTitle: '¡No deberías estar aquí!',
        accessDeniedMessage: 'Este rincón es solo para roles staff (admin, manager o sales). Pero no te preocupes, te devolvemos a tu rumbo en un momento.',
        accessDeniedRedirect: 'Llevándote a tu cuenta en {seconds} segundos…',
        accessDeniedGoToAccount: 'Ir a mi cuenta',
        generateWithAI: 'Generar con IA',
        generateTopicLabel: 'Tema del artículo',
        generateTopicPlaceholder: 'Ej: cómo una pyme puede vender más con web + WhatsApp',
        generateTopicHelp: 'Describe el objetivo de negocio del artículo (leads, conversión, presencia digital, automatización, etc.).',
        generating: 'Generando artículo...',
        generateError: 'Error al generar el artículo.',
        generateSuccess: 'Borrador creado. Revisa, añade imagen si quieres y publica.',
        generateLanguageLabel: 'Idioma del artículo',
        generateLanguageEs: 'Español',
        generateLanguageEn: 'English',
        generateLanguageHelp: 'Genera el mismo tema en otro idioma para tener versiones ES/EN.',
        usageTitle: 'Uso de la generación (Groq)',
        promptSentTitle: 'Prompt enviado al LLM',
        promptSentSystem: 'Prompt de sistema',
        promptSentUser: 'Prompt del usuario',
        relatedTitleLabel: 'Título real (para enlace Calificar)',
        relatedTitlePlaceholder: 'Ej. El padrino',
        relatedYearLabel: 'Año (para enlace Calificar)',
        relatedYearPlaceholder: '1972',
        usagePromptTokens: 'Tokens de entrada',
        usageCompletionTokens: 'Tokens de salida',
        usageTotalTokens: 'Tokens totales',
        usageModel: 'Modelo',
        usageResponseTime: 'Tiempo de respuesta',
        postLanguage: 'Idioma del artículo (URL)',
        postLanguageHelp: 'ES: /noticias/actualidad|tecnologia|negocios/... — EN: /news/news|tech|business/...',
        searchPlaceholder: 'Buscar por título, slug o resumen...',
        searchButton: 'Buscar',
        filterStatus: 'Estado',
        filterStatusAll: 'Todos',
        filterStatusPublished: 'Publicados',
        filterStatusDraft: 'Borradores',
        filterCategory: 'Tipo',
        filterCategoryAll: 'Todos los tipos',
        searchImagesOnGoogle: 'Buscar imágenes grandes en Google',
        page: 'Página',
        prev: 'Anterior',
        next: 'Siguiente',
        showingCount: 'Mostrando {from}-{to} de {total}',
        mightAlsoLike: 'También podría interesarte'
      },
      loadingModalDefaultTitle: 'Generando recomendaciones...',
      loadingModalDefaultMessage: 'Conectando con el servidor de IA...',
      loadingModalCloseAndReload: 'Cerrar y recargar',
      loadingModalConnecting: 'Conectando con Open WebUI...',
      loadingModalAnalyzing: 'Analizando tus preferencias...',
      loadingModalGenerating: 'Generando recomendaciones personalizadas...',
      loadingModalDetecting: 'Detectando duplicados...',
      loadingModalSaving: 'Guardando recomendaciones actualizadas...'
    }
  },
  en: {
    // Navigation
    nav: {
      brand: 'Es tu mejor opción',
      home: 'Home',
      allNews: 'All news',
      blogAdmin: 'Dashboard',
      login: 'Sign in',
      account: 'Account',
      panel: 'Dashboard',
      menuToggleAria: 'Open or close navigation menu'
    },
    blogAdmin: {
      groqSectionTitle: 'Groq (AI article generation)',
      groqSectionHelp:
        'Save your API key in this panel to use “Generate with AI” for articles. AI credentials are not read from server environment variables—only from the database.',
      saveGroqKey: 'Save key',
      groqKeySaved: 'Key saved successfully.',
      groqKeyError: 'Could not save the key.',
      checkGroqQuota: 'Check quota',
      groqQuotaLoading: 'Checking…',
      groqQuotaUnavailable: 'No quota data',
      groqQuotaOk: 'Groq is reachable',
      groqQuotaOkRemaining: '{remaining} of {limit} requests remaining',
      groqQuotaOkRemainingPartial: '{remaining} requests remaining (limit not reported by API)',
      groqQuota401Hint:
        '401 Unauthorized: invalid, revoked, or mismatched key. With Groq as the active provider, the panel key is used; the user groq_api_key column also applies for Groq. Create a new key (gsk_…) at console.groq.com, paste it without spaces or quotes, and save again.',
      groqQuotaLimitReached: 'Groq request limit reached for now.',
      groqQuotaPaymentRequired: 'Groq requires payment or the free tier is exhausted.',
      groqQuotaNotConfigured: 'Groq API key is not configured.',
      groqQuotaVerifyFailed: 'Could not verify Groq quota.',
      catalogSectionTitle: 'Product catalog',
      catalogSectionHelp: 'Manage categories and products (name, price, category) from this panel.',
      categoriesTitle: 'Categories',
      productsTitle: 'Products',
      categorySlugPlaceholder: 'Category slug (e.g. hosting)',
      categoryNamePlaceholder: 'Category name',
      categoryDescPlaceholder: 'Description (optional)',
      saveCategory: 'Save category',
      categorySaved: 'Category saved.',
      productNamePlaceholder: 'Product name',
      productSlugPlaceholder: 'Product slug',
      productDescPlaceholder: 'Product description',
      pricePlaceholder: 'Price',
      productPriceLabel: 'Current price (sale)',
      productComparePriceLabel: 'Reference price',
      productComparePricePlaceholder: 'e.g. 100',
      productComparePriceHelp: 'Optional. If higher than the current price, shown as an offer with discount %.',
      productCurrencyLabel: 'Currency',
      saveProduct: 'Save product',
      productSaved: 'Product saved.',
      productDeleted: 'Product deleted.',
      noProducts: 'No products found.',
      noCategoryOption: 'No category',
      productTableName: 'Product',
      productTableCategory: 'Category',
      productTablePrice: 'Price',
      productTableEnabled: 'In catalog',
      productActiveOn: 'Active',
      productActiveOff: 'Inactive',
      productFormActiveLabel: 'Show in public catalog',
      productFormDirectCheckoutLabel: 'Show “Pay now” button',
      productFormDirectCheckoutHelp:
        'When enabled, the product shows direct checkout on the homepage and product page (requires PayPal or Binance configured).',
      productTableDirectCheckout: 'Pay now',
      viewProduct: 'View product page',
      productPreviewNote: 'Preview (inactive product)',
      productDirectCheckoutOn: 'Yes',
      productDirectCheckoutOff: 'No',
      productDirectCheckoutUpdateError: 'Could not update product direct checkout.',
      productActiveUpdateError: 'Could not update product availability.',
      productIconLabel: 'Product icon',
      productIconHelp: 'Shown centered above the title in the homepage product listing.',
      productIconPickerLabel: 'Icon',
      productIconColorLabel: 'Color',
      productIconRocket: 'Rocket (launch)',
      productIconChartLine: 'Chart (growth)',
      productIconLayerGroup: 'Layers (scale)',
      productIconCogs: 'Cogs (custom build)',
      productIconGlobe: 'Globe (domains)',
      productIconCloud: 'Cloud (hosting)',
      productIconServer: 'Server (VPS)',
      productIconAward: 'Medal (plan tier)',
      productIconCrown: 'Crown (premium)',
      productIconTools: 'Tools',
      productIconWrench: 'Wrench (maintenance)',
      productIconShoppingCart: 'Shopping cart',
      productIconWhatsapp: 'WhatsApp',
      productIconCreditCard: 'Credit card (payments)',
      productIconBoxes: 'Boxes (inventory)',
      productIconFileInvoice: 'Invoice',
      productIconCashRegister: 'Cash register (POS)',
      productIconChartBar: 'Bar chart (KPI)',
      productIconBoxOpen: 'Generic box',
      productIconColorCyan: 'Cyan',
      productIconColorSky: 'Sky',
      productIconColorViolet: 'Violet',
      productIconColorAmber: 'Amber',
      productIconColorGold: 'Gold',
      productIconColorCopper: 'Copper',
      productIconColorSilver: 'Silver',
      productIconColorEmerald: 'Emerald',
      productIconColorRose: 'Rose',
      productIconColorLime: 'Lime',
      confirmDeleteProduct: 'Delete this product?',
      catalogLoadError: 'Could not load catalog.',
      catalogSaveError: 'Could not save catalog.',
      catalogDeleteError: 'Could not delete product.',
      catalogRequiredFields: 'Complete required fields before saving.',
      testimonialsSectionTitle: 'Client testimonials',
      testimonialsSectionHelp:
        'Manage reviews shown in the Clients section on the homepage. Country is displayed with its flag.',
      testimonialQuoteEsLabel: 'Quote (Spanish)',
      testimonialQuoteEnLabel: 'Quote (English)',
      testimonialNameLabel: 'Name',
      testimonialRoleEsLabel: 'Role / activity (Spanish)',
      testimonialRoleEnLabel: 'Role / activity (English)',
      testimonialCountryLabel: 'Country',
      testimonialInitialLabel: 'Avatar initial',
      testimonialSortOrderLabel: 'Sort order',
      saveTestimonial: 'Save testimonial',
      testimonialSaved: 'Testimonial saved.',
      testimonialDeleted: 'Testimonial deleted.',
      noTestimonials: 'No testimonials yet.',
      confirmDeleteTestimonial: 'Delete this testimonial?',
      testimonialsLoadError: 'Could not load testimonials.',
      testimonialsSaveError: 'Could not save testimonial.',
      testimonialsDeleteError: 'Could not delete testimonial.',
      testimonialsRequiredFields: 'Quote (ES/EN) and name are required.',
      testimonialActiveOn: 'Visible',
      testimonialActiveOff: 'Hidden',
      testimonialActiveUpdateError: 'Could not update testimonial visibility.',
      testimonialTableName: 'Client',
      testimonialTableCountry: 'Country',
      testimonialTableOrder: 'Order',
      testimonialTableEnabled: 'On homepage',
      countryPy: 'Paraguay',
      countryBo: 'Bolivia',
      countryPe: 'Peru',
      countryCo: 'Colombia',
      countryAr: 'Argentina',
      countryCl: 'Chile',
      countryUy: 'Uruguay',
      countryBr: 'Brazil',
      countryEc: 'Ecuador',
      countryVe: 'Venezuela',
      countryMx: 'Mexico',
      countryCr: 'Costa Rica',
      countryPa: 'Panama',
      countryUs: 'United States',
      countryEs: 'Spain',
      cancelEdit: 'Cancel edit',
      productPrimaryImage: 'Primary image (share preview)',
      productPrimaryImageHelp: 'Shown on WhatsApp, Facebook, and other networks when sharing the product URL. Recommended: 1200×630 px.',
      productGalleryImages: 'Product gallery',
      setAsPrimaryImage: 'Set as primary',
      removeImage: 'Remove',
      productImageUploadError: 'Could not upload the image.',
      cardPostsTitle: 'Content management',
      cardPostsDesc: 'Create, edit, publish, and rank business-focused digital content.',
      cardAiTitle: 'AI engine',
      cardAiDesc: 'Configure provider, model, and credentials for commercial content workflows.',
      cardSettingsTitle: 'Homepage and site',
      cardSettingsDesc: 'Toggle homepage sections, visual theme, and block-level layouts.',
      cardCatalogTitle: 'Commercial catalog',
      cardCatalogDesc: 'Manage categories, products, and pricing.',
      cardSalesTitle: 'Sales quoter',
      cardSalesDesc: 'Commercial wizard to estimate scope, complexity, and investment range.',
      cardWhatsappTitle: 'WhatsApp',
      cardWhatsappDesc: 'Connect a line, scan the QR, and chat with clients from the dashboard.',
      quickStatusWhatsapp: 'WhatsApp',
      welcomeTitle: 'Commercial management dashboard',
      welcomeDesc: 'Manage content, AI, site appearance, and catalog to improve conversion and digital presence.',
      quickStatusContent: 'Content',
      quickStatusAi: 'AI',
      quickStatusSite: 'Site',
      quickStatusCatalog: 'Catalog',
      quickStatusSales: 'Quoter',
      quoteWizardTitle: 'Commercial quote wizard',
      quoteWizardHelp: 'Use this flow for faster and more accurate consultative sales estimates.',
      quoteStepScope: 'Scope',
      quoteStepComplexity: 'Complexity',
      quoteStepResult: 'Result',
      quoteProjectType: 'Project type',
      quoteProjectWebsite: 'Website',
      quoteProjectSoftware: 'Custom software',
      quoteWebsiteType: 'Website type',
      quoteWebsiteLanding: 'Landing',
      quoteWebsiteCorporate: 'Corporate',
      quoteWebsiteEcommerce: 'E-commerce',
      quoteModules: 'Number of modules',
      quoteIntegrations: 'Number of integrations',
      quoteComplexity: 'Technical complexity',
      quoteComplexityLow: 'Low',
      quoteComplexityMedium: 'Medium',
      quoteComplexityHigh: 'High',
      quoteTimeline: 'Delivery speed',
      quoteTimelineStandard: 'Standard',
      quoteTimelineFast: 'Fast-track',
      quoteTimelineUrgent: 'Urgent',
      quoteSupportMonths: 'Support months',
      quoteAutomation: 'Include automations',
      quoteSeo: 'Include initial SEO',
      quoteUxResearch: 'Include UX discovery',
      quoteEstimatedRange: 'Estimated range',
      quoteComplexityScore: 'Complexity score',
      quoteAssumptionOne: 'Estimate based on initial functional scope and standard assumptions.',
      quoteAssumptionTwo: 'Third-party costs are excluded (licenses, gateways, or enterprise infrastructure).',
      quoteAssumptionThree: 'Finalize after technical and commercial discovery.',
      quoteBack: 'Back',
      quoteNext: 'Next',
      quoteCalculate: 'Calculate range',
      quoteRestart: 'Restart wizard',
      quoteSave: 'Save quote',
      quoteSaved: 'Quote saved successfully.',
      quoteSaveError: 'Could not save the quote.',
      quoteStageUpdated: 'Stage updated successfully.',
      quoteStageUpdateError: 'Could not update stage.',
      quotePipelineTitle: 'Commercial pipeline',
      quotePipelineRefresh: 'Refresh pipeline',
      quotePipelineLoading: 'Loading pipeline...',
      quotePipelineEmpty: 'No quotes registered yet.',
      quoteTableId: 'ID',
      quoteTableProject: 'Project',
      quoteTableRange: 'Range',
      quoteTableStage: 'Stage',
      quoteTableOwner: 'Owner',
      quoteTableNotes: 'Notes',
      quoteFilterAllStages: 'All stages',
      quoteFilterAllProjects: 'All projects',
      quoteFilterAllOwners: 'All owners',
      quoteFilterSearch: 'Search by contact, company, or notes...',
      quoteUnassigned: 'Unassigned',
      quoteNotesPlaceholder: 'Commercial follow-up...',
      quoteUpdated: 'Quote updated.',
      quoteUpdateError: 'Could not update the quote.',
      quoteStageNew: 'New',
      quoteStageQualified: 'Qualified',
      quoteStageProposal: 'Proposal',
      quoteStageWon: 'Won',
      quoteStageLost: 'Lost',
      quoteExportCsv: 'Export CSV',
      quoteExportCrm: 'Export CRM JSON',
      quoteExportCsvOk: 'CSV export generated.',
      quoteExportCrmOk: 'CRM export generated.',
      quoteExportError: 'Could not export quote data.',
      quoteRunReminders: 'Run reminders',
      quoteRemindersRunOk: 'Reminders executed',
      quoteRemindersRunError: 'Could not run reminders.',
      contentTypeBusiness: 'Business',
      contentTypeTech: 'Tech',
      contentTypeNews: 'News',
      templateActionsTitle: 'Quick commercial templates',
      templateApplyCaseStudy: 'Apply business template',
      templateApplyStrategy: 'Apply tech template',
      templateApplyTactical: 'Apply news template',
      templateAppliedMessage: 'Template applied to draft.',
      templateContentCaseStudy: '<h2>Business context and objective</h2><p>Describe the business scenario, target segment, and desired result.</p><h2>Current friction points</h2><ul><li>Main conversion blocker</li><li>Visibility or trust gap</li><li>Operational bottleneck</li></ul><h2>Execution roadmap</h2><ol><li>Short-term actions (0-2 weeks)</li><li>Mid-term improvements (3-6 weeks)</li><li>KPI monitoring cadence</li></ol><h2>Expected impact</h2><p>Summarize lead quality, conversion lift, and operational gains.</p>',
      templateContentStrategy: '<h2>Strategic thesis</h2><p>Define why this strategy matters now and what commercial metric it should move.</p><h2>Core framework</h2><ul><li>Positioning and message architecture</li><li>Acquisition channels</li><li>Conversion and trust layers</li></ul><h2>Implementation checklist</h2><ul><li>Owners and timeline</li><li>Required assets</li><li>Tracking and attribution setup</li></ul><h2>Decision criteria</h2><p>Clarify what signals indicate scale, iterate, or stop.</p>',
      templateContentTactical: '<h2>Quick tactical objective</h2><p>State the immediate conversion goal for this tactic.</p><h2>Step-by-step execution</h2><ol><li>Setup (tools and baseline)</li><li>Launch actions</li><li>Optimization loop</li></ol><h2>Copy examples</h2><ul><li>Primary CTA variant A</li><li>Primary CTA variant B</li><li>Follow-up message</li></ul><h2>Metrics to watch</h2><p>CTR, lead completion rate, and sales-qualified conversations.</p>',
      aiSectionTitle: 'AI configuration',
      aiSectionHelp: 'Set provider, model, region, and credentials for content generation.',
      siteSettingsTitle: 'General site settings',
      siteSettingsHelp: 'Set template/theme and visual layout variants by section (hero, offers, and CTA).',
      saveSiteSettings: 'Save site settings',
      siteSettingsSaved: 'Site settings saved successfully.',
      saveContactSettings: 'Save public contact',
      contactSettingsSaved: 'Public contact saved successfully.',
      contactSettingsSaveError: 'Could not save public contact.',
      postSavedSuccess: 'Article saved successfully.',
      postDeletedSuccess: 'Article deleted successfully.',
      postGeneratedSuccess: 'Draft generated successfully with AI.',
      generateTopicRequired: 'Enter a topic to generate the article.',
      postRequiredFields: 'Title and content are required.',
      aiProviderLabel: 'AI provider',
      aiModelLabel: 'Model',
      aiRegionLabel: 'Region',
      aiApiKeyLabel: 'Provider API key',
      aiTimeoutLabel: 'Provider timeout (ms)',
      aiTemperatureLabel: 'AI temperature',
      aiFallbackOrderLabel: 'Fallback order',
      aiFallbackOrderHelp: 'If primary provider fails, the system retries in this order.',
      providerHealthRefresh: 'Refresh provider health',
      providerHealthLoading: 'Checking health...',
      providerMatrixTitle: 'Provider matrix',
      providerMatrixProvider: 'Provider',
      providerMatrixModel: 'Model',
      providerMatrixTimeout: 'Timeout',
      providerMatrixStatus: 'Status',
      providerPrimaryBadge: 'Primary',
      providerStatusAvailable: 'Available',
      providerStatusUnavailable: 'Unavailable',
      uiThemeLabel: 'Template / Theme',
      uiThemeHelp: 'Select the active visual template for landing and commercial sections.',
      themeAurora: 'Aurora',
      themeSunset: 'Sunset',
      themeEmerald: 'Emerald',
      uiHeroLayoutLabel: 'Hero layout',
      uiOffersLayoutLabel: 'Offers layout',
      uiCtaLayoutLabel: 'CTA layout',
      uiLayoutDefault: 'Default',
      uiLayoutCompact: 'Compact',
      uiLayoutSpotlight: 'Spotlight',
      uiLayoutCards: 'Highlighted cards',
      uiLayoutMinimal: 'Minimal',
      uiLayoutSplit: 'Split',
      uiLayoutBanner: 'Banner',
      resetSiteDefaults: 'Reset defaults',
      siteSettingsDefaultsApplied: 'Default values applied (theme + layouts). Save to persist them.',
      landingSectionsTitle: 'Homepage visible sections',
      landingSectionsHelp: 'Toggle landing blocks on or off. Changes apply to the public site for all visitors.',
      landingSectionHero: 'Main hero (headline and CTAs)',
      landingSectionEncajaBanner: 'Encaja star product banner',
      landingSectionHeroStats: 'Stats strip below hero',
      landingSectionDomainSearch: 'Domain search',
      landingSectionServices: 'Services grid and contact form',
      landingSectionOffers: 'Product catalog / packages',
      landingSectionConversionProof: 'Conversion proof (block below products)',
      landingSectionProcess: 'Work process',
      landingSectionWhyChoose: 'Why choose us',
      landingSectionTestimonials: 'Client testimonials',
      landingSectionNews: 'Latest news',
      landingSectionFaq: 'FAQ',
      landingSectionFinalCta: 'Final CTA',
      landingSectionWhatsappFab: 'Floating WhatsApp button',
      landingSectionMobileStickyCta: 'Mobile sticky bar (WhatsApp / login)',
      landingSectionFooter: 'Footer',
      landingSectionsDefaultsApplied: 'Compact layout restored (domains + products + news). Save to persist.',
      landingSectionsResetCompact: 'Compact layout',
      languageSelectorEnabledLabel: 'Show language selector (ES / EN)',
      languageSelectorEnabledHelp:
        'Off: the site stays in Spanish and the menu selector is hidden. Turn on when you want to offer English.',
      saveLandingSections: 'Save homepage sections',
      siteSettingsThemeBlockTitle: 'Theme and visual layouts',
      siteSettingsThemeBlockHelp: 'Global appearance settings. Saved with the button above as well.',
      themeAccessibilityTitle: 'Quick accessibility check (AA)',
      themeAccessibilityPass: 'Base contrast looks good for readability on current theme.',
      themeAccessibilityWarn: 'Low contrast detected in at least one base combination.',
      themeCheckprimaryOnSurface: 'Primary on surface',
      themeChecktextOnSurface: 'Base text on surface',
      contactSettingsTitle: 'Public contact (superadmin)',
      contactSettingsHelp: 'WhatsApp, email, and support notes visible on the landing page.',
      contactWhatsappPyLabel: 'WhatsApp Paraguay',
      contactWhatsappBoLabel: 'WhatsApp Bolivia',
      contactWhatsappPyPlaceholder: '595983062031',
      contactWhatsappBoPlaceholder: '59177499919',
      contactEmailLabel: 'Contact email',
      contactEmailPlaceholder: 'contact@estumejoropcion.com',
      contactSupportEsLabel: 'Support note (ES)',
      contactSupportEnLabel: 'Support note (EN)',
      contactSupportEsPlaceholder: 'Atención en inglés por mensaje.',
      contactSupportEnPlaceholder: 'English support is available by conversation.'
    },
    whatsappAdmin: {
      loading: 'Loading WhatsApp…',
      errorGeneric: 'Something went wrong.',
      notConfiguredServer: 'Server is missing WHATSAPPAPI_SERVER and WHATSAPP_TOKEN in .env. Ask your hosting admin to configure them.',
      tabConnection: 'Connection',
      tabChats: 'Chats',
      connectionTitle: 'WhatsApp line',
      connectionHelp: 'Register a new number or reconnect the active line. Scan the QR with the phone that will own this line.',
      refresh: 'Refresh',
      lineIdLabel: 'Line ID: {id}',
      phoneLabel: 'Phone: {phone}',
      registerNewLine: 'Register new number',
      startSession: 'Start session',
      newQr: 'New QR',
      disconnect: 'Disconnect',
      disconnectConfirm: 'Disconnect this WhatsApp session?',
      disconnected: 'Session disconnected.',
      noLineYet: 'No active line yet. Create one to get started.',
      selectLine: 'Active line for this site',
      lineCreated: 'Line created. Scan the QR if shown.',
      sessionStarted: 'Session started.',
      conversations: 'Chats',
      noConversations: 'No inbound messages in the last 7 days yet.',
      selectConversation: 'Select a conversation to view history.',
      messagePlaceholder: 'Type a message…',
      send: 'Send',
      createLineTitle: 'Register new line',
      lineNameLabel: 'Line name',
      lineNamePlaceholder: 'e.g. EstuMejorOpcion Sales',
      cancel: 'Cancel',
      createAndStart: 'Create and start',
      scanQrTitle: 'Scan QR code',
      scanQrHelp: 'On the phone: Linked devices → Link a device.',
      unknownContact: 'Contact',
      multiLineTitle: 'WhatsApp lines',
      multiLineHelp: 'Register one line per country or channel. Each has an id (e.g. paraguay, bolivia) to chat separately.',
      noLinesRegistered: 'No lines registered in this panel yet.',
      lineIdSlugLabel: 'Identifier (slug)',
      lineIdSlugPlaceholder: 'paraguay',
      lineIdSlugHelp: 'Lowercase letters, numbers, hyphens only. E.g. paraguay, bolivia',
      lineLabelField: 'Display label',
      lineLabelPlaceholder: 'Paraguay',
      lineNameWhaticketHelp: 'Name on Whaticket (internal).',
      presetParaguay: 'Paraguay preset',
      presetBolivia: 'Bolivia preset',
      chatAsLine: 'Chat as line',
      conversationsFor: 'Chats — {label}',
      chatLineNotConnected: 'This line is not connected. Go to Connection and start the session.',
      showQr: 'Show QR',
      removeFromPanel: 'Remove from panel',
      openChats: 'Open chats →',
      scanQrFor: 'QR — {label}',
      sessionStartedFor: 'Session started: {label}',
      disconnectConfirmFor: 'Disconnect line {label}?',
      disconnectedFor: 'Disconnected: {label}',
      removeLineConfirm: 'Remove {label} from panel? (Does not delete the line on Whaticket)',
      newConversation: 'New chat',
      newConversationTitle: 'Start conversation',
      newConversationNumber: 'WhatsApp number',
      newConversationNumberPlaceholder: '59170000000',
      newConversationNumberHelp: 'Country code + number, digits only. E.g. 59177490191 (Bolivia) or 595981234567 (Paraguay).',
      newConversationIsGroup: 'This is a WhatsApp group',
      newConversationStart: 'Open chat',
      newChatHint: 'New conversation — send the first message',
      messageSent: 'Message sent.',
      missingPhoneForSend: 'Could not resolve this chat’s phone number. Use “New chat” and enter the number manually.',
      invalidPhoneNumber: 'Enter a valid number (at least 8 digits, with country code).',
      qrExpiresHint: 'The code refreshes automatically every ~20 seconds. If WhatsApp says “invalid”, wait for the on-screen code to change and scan again. Link one line at a time.',
      lineConnected: 'Line {label} connected successfully.',
      linkOneLineHint: 'To link: pick one line, tap “Start session”, and scan when the QR appears. Do not open Paraguay and Bolivia QR at the same time.',
      errWappCheckContact: 'Could not verify the number with WhatsApp. Ensure the line shows connected, the number includes the country code, and the contact uses WhatsApp.',
      errWappInvalidContact: 'That number is not on WhatsApp or the format is wrong. Use digits only with country code (e.g. 59177490191).',
      errWappNotInitialized: 'The WhatsApp line is not ready yet. Go to Connection, wait until it shows Connected, then try again.',
      errWappDisabled: 'This line is disabled on Whaticket.',
      sendTimeout: 'Send timed out. The line may be reconnecting; wait a few seconds and try again.',
      sendingMessage: 'Sending…',
      attachFile: 'Attach file',
      removeAttachment: 'Remove',
      messageOrMediaRequired: 'Type a message or attach a file.',
      previewImage: '📷 Image',
      previewAudio: '🎤 Audio',
      previewVideo: '🎬 Video',
      previewDocument: '📄 Document',
      previewFile: '📎 File',
      previewLink: '🔗 Link',
      openFile: 'Open file',
      emptyMessage: '(empty message)',
    },
    userAuth: {
      pageTitle: 'Account',
      pageSubtitle: 'Sign in or register. New users get the customer role.',
      loginTitle: 'Sign in',
      signupTitle: 'Sign up',
      email: 'Email',
      emailPlaceholder: 'you@example.com',
      nameOptional: 'Name (optional)',
      namePlaceholder: 'Your name',
      password: 'Password',
      passwordPlaceholder: 'Your password',
      passwordCreatePlaceholder: 'At least 6 characters',
      confirmPassword: 'Confirm password',
      confirmPasswordPlaceholder: 'Repeat password',
      loginSubmit: 'Sign in',
      signupSubmit: 'Create account',
      noAccountSignup: 'No account yet? Sign up',
      haveAccountLogin: 'Already have an account? Sign in',
      or: 'Or',
      continueWithGoogle: 'Continue with Google',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      errorPasswordMismatch: 'Passwords do not match.',
      errorPasswordShort: 'Password must be at least 6 characters.',
      errorSignInGeneric: 'Could not sign in.',
      errorRegisterGeneric: 'Could not complete sign up.',
      dashboardTitle: 'My account',
      dashboardSubtitle: 'Review your purchases and special offers.',
      purchasesTitle: 'Purchase history',
      offersTitle: 'Special offers',
      noPurchases: 'You do not have purchases yet.',
      noOffers: 'No active offers right now.',
      offersDiscountHint: 'Catalog products with a reference price and an active discount.',
      viewOffer: 'View offer',
      offerPayNow: 'Pay now',
      personalCouponsTitle: 'Personal coupons and promos',
      goToAdmin: 'Go to management panel',
      logout: 'Sign out'
    },
    checkoutDomain: {
      title: 'Domain registration checkout',
      intro:
        'We re-check price and availability at payment time. After payment is confirmed, we record your order so registration and DNS can be completed.',
      backHome: 'Back to home',
      missingDomain: 'No domain in the URL. Go home and pick an option from the table.',
      loginRequiredTitle: 'Sign in to continue',
      loginRequiredBody: 'You need an account to pay and save your purchase.',
      goToLogin: 'Go to sign in',
      loadingQuote: 'Fetching quote…',
      labelDomain: 'Domain',
      labelAmount: 'Total',
      amountUsd: 'USD {amount}',
      quoteDisclaimer:
        'This is the final selling price applied to your domain purchase.',
      payWithPayPal: 'Pay with PayPal',
      payWithBinanceWallet: 'Pay with USDT deposit (Binance)',
      noGateways: 'Payment methods are not configured on the server.',
      payPalCancelled: 'You cancelled PayPal. You can try again.',
      binanceInstructionsTitle: 'Send USDT to this deposit address',
      binanceScanQr: 'Scan the QR code with Binance or another compatible wallet',
      binanceDownloadQr: 'Download QR',
      binanceQrHint: 'You can also save the image and scan it from your phone gallery.',
      binanceReference: 'Internal reference:',
      binanceNetwork: 'Network:',
      binanceAmount: 'Exact amount (USDT):',
      binanceAmountHint:
        'Approximate match to your USD total ({usd}). It must match the displayed amount so we can link the payment.',
      binanceAddress: 'Address:',
      binanceTag: 'Memo / Tag:',
      binancePolling: 'We detect deposits automatically. This page updates when funds arrive.',
      successTitle: 'Payment confirmed',
      successBody: 'We recorded your purchase for {domain}.',
      fulfillmentNote:
        'Registering the domain at the provider and DNS setup may need a manual step; we will contact you if needed.',
      goToAccount: 'Go to my account',
      errorQuote: 'Could not fetch a quote. Try again.',
      errorPriceUnavailable: 'No price is available for this domain right now.',
      errorNotAvailable: 'This domain is not available for registration.',
      errorPayPalStart: 'Could not start PayPal.',
      errorPayPalCapture: 'Could not confirm PayPal payment.',
      errorBinanceStart: 'Could not prepare the deposit address.',
      errorBinancePoll: 'Deposit not detected yet.'
    },
    // Recommendations
    recommendations: {
      title: 'Recommendations',
      loading: 'Loading recommendations...',
      loadingMovies: 'Loading movies...',
      loadingSeries: 'Loading series...',
      loadingMusic: 'Loading music...',
      noRecommendations: 'You have no recommendations yet',
      welcomeNoRecommendations: 'Welcome! You have no recommendations yet',
      noRecommendationsOfType: 'You have no {type} recommendations yet',
      generateFirst: 'To get started, you can generate your first recommendations based on your preferences and content from other users.',
      generateType: 'You can generate {type} recommendations based on your preferences and content from other users.',
      generateFirstButton: 'Generate recommendations',
      generateTypeButton: 'Generate {type} recommendations',
      generating: 'Generating recommendations...',
      generatingFirst: 'Generating…',
      generateFirstRecommendations: 'Generate first recommendations',
      moviesRecommended: 'Recommended Movies',
      seriesRecommended: 'Recommended Series',
      musicRecommended: 'Recommended Music',
      moviesAvailable: '{count} movies available',
      seriesAvailable: '{count} series available',
      musicAvailable: '{count} songs/albums available',
      noMoviesAvailable: 'No movies available',
      noSeriesAvailable: 'No series available',
      noMusicAvailable: 'No music available',
      noMoviesFound: 'No recommended movies found. Try refreshing the page.',
      noSeriesFound: 'No recommended series found. Try refreshing the page.',
      noMusicFound: 'No recommended music found. Try refreshing the page.',
      watched: 'Watched',
      listened: 'Listened',
      watchedOn: 'Watched on:',
      listenedOn: 'Listened on:',
      yourRating: 'Your rating:',
      rating: 'Rating:',
      markAsWatched: 'Mark as Watched',
      markAsListened: 'Mark as Listened',
      alreadyWatched: 'Already Watched',
      alreadyListened: 'Already Listened',
      seasons: 'seasons',
      retry: 'Retry',
      noResultsWithFilters: 'No recommendations found with the applied filters.',
      searchMusicTitle: 'Search music to mark as listened',
      searchMusicDescription: 'Search songs by name/artist or explore by genre (cumbia, rock, pop, reggaeton, etc.). Mark them as listened and recommendations will be regenerated with AI.',
      searchMoviesTitle: 'Search {type} to mark as watched',
      searchMoviesDescription: 'Find a movie or series, mark it as watched with your rating and recommendations will be regenerated with AI.',
      results: 'Results',
      allGenresOption: 'All genres',
      markAsWatched: 'Mark as Watched',
      omit: 'Omit',
      reactivate: 'Reactivate',
      omitted: 'Omitted',
      new: 'New',
      searchPlaceholder: 'Search recommendations...',
      allTypes: 'All types',
      movies: 'Movies',
      series: 'Series',
      music: 'Music',
      allGenres: 'All genres',
      filteringBy: 'Filtering by',
      active: 'Active',
      omitted: 'Omitted',
      all: 'All',
      favorites: 'Favorites',
      favoritesActive: 'Active Favorites',
      favoritesOmitted: 'Omitted Favorites',
      addToFavorites: 'Add to favorites',
      removeFromFavorites: 'Remove from favorites',
      sortBy: 'Sort by',
      averageRating: 'Average Rating',
      externalRating: 'External Rating',
      aiRating: 'AI Rating',
      userRating: 'User Rating',
      externalRatingLabel: 'External Rating',
      aiRatingLabel: 'AI Rating',
      averageLabel: 'Average',
      artist: 'Artist',
      actors: 'Actors',
      recommendedAt: 'Recommended',
      whyRecommended: 'Why is this recommended?',
      confirmOmit: 'Omit "{title}"? It will not appear in future recommendation generations.',
      errorLoading: 'Error loading recommendations.',
      errorGenerating: 'Error generating {type} recommendations. Check your Groq configuration in Settings.',
      couldNotGenerate: 'Could not generate {type} recommendations. Check your Groq configuration in Settings or add watched content first.',
      refresh: 'Refresh',
      exploreContent: 'Explore recommended movies, series and music. Mark as watched/listened and rate your experience.',
      processing: 'Processing...',
      results: 'Results',
      clearFilter: 'Remove filter',
      errorSearching: 'Search error',
      enterSongOrGenre: 'Enter a song/artist name or select a genre',
      movieNamePlaceholder: 'Movie name...',
      seriesNamePlaceholder: 'Series name...',
      songOrArtistPlaceholder: 'Song or artist name...',
      typeMovies: 'movies',
      typeSeries: 'TV series',
      typeMusic: 'music',
      typeTVSeries: 'TV series'
    },
    // History
    history: {
      title: 'History',
      loading: 'Loading history...',
      errorLoading: 'Error loading history',
      errorUpdatingRating: 'Error updating rating',
      errorDeletingContent: 'Error deleting content',
      watched: 'Watched',
      listened: 'Listened',
      yourRating: 'Your rating',
      edit: 'Edit',
      delete: 'Delete',
      returnToRecommendations: 'Return to Recommendations',
      confirmDelete: 'Delete from history?',
      deleteNote: 'This content will reappear in recommendations with your current rating',
      deleteAndReturn: 'Delete and Return to Recommendations',
      noContent: 'No content in history',
      noContentWatched: 'No {type} content',
      whenYouMark: 'When you mark content as {type}, it will appear here.',
      tabs: {
        movies: 'Movies',
        series: 'Series',
        music: 'Music'
      },
      manageHistory: 'Manage your history of watched and listened content. Edit ratings or return content to recommendations.',
      totalElements: '{count} elements in total',
      confirmDeleteTitle: 'Confirm Deletion',
      confirmDeleteMessage: 'Are you sure you want to delete {title} from your history?',
      deleteNoteInfo: 'This content will reappear in recommendations with your current rating ({rating}) and you can mark it as watched again.',
      actionCannotBeUndone: 'This action cannot be undone.',
      retry: 'Retry',
      notes: 'Notes',
      searchPlaceholder: 'Search by title, artist, director, genre...',
      clearSearch: 'Clear search',
      noResults: 'No results found for "{query}"',
      showingResults: 'Showing {filtered} of {total}'
    },
    // Statistics
    stats: {
      title: 'Statistics',
      loading: 'Loading statistics...',
      noStatsAvailable: 'No statistics available',
      markContentToSeeStats: 'Mark content as watched to see your statistics.',
      totalWatched: 'Total watched / listened',
      movies: 'Movies',
      series: 'Series',
      music: 'Music listened',
      thisMonth: 'This month',
      titlesWatched: 'title(s) watched / listened',
      averageRating: 'Average rating',
      fromAllWatched: 'From everything you marked as watched or listened',
      distributionByType: 'Distribution by type',
      noContentByType: 'No content watched by type yet.',
      favoriteGenres: 'Favorite genres',
      markContentToSeeGenres: 'Mark movies, series or music as watched/listened to see your favorite genres.',
      yourRatings: 'Your ratings (1-10)',
      noRatingsYet: 'No ratings yet.',
      monthlyActivity: 'Monthly activity',
      noMonthlyActivity: 'No monthly activity yet. Mark content as watched to see your history.',
      mostListenedArtists: 'Most listened artists',
      movies: 'Movies',
      series: 'Series',
      music: 'Music'
    },
    // Settings
    settings: {
      title: 'Settings',
      preferencesTitle: 'Preferences Configuration',
      language: 'Language',
      languageDescription: 'Change the language of the interface and AI prompts',
      spanish: 'Español',
      english: 'English',
      preferences: 'Preferences',
      favoriteGenres: 'Favorite Genres',
      favoriteGenresExample: '(e.g.: Action, Drama, Comedy, Horror)',
      favoriteGenresDescription: 'Thematic categories you like (Action, Drama, Comedy, Horror, etc.)',
      favoriteActors: 'Favorite Actors',
      preferredMovieTypes: 'Preferred Movie Types',
      preferredMovieTypesExample: '(e.g.: Blockbuster, Indie, Classics)',
      preferredMovieTypesDescription: 'Style or type of production you prefer (Blockbuster, Indie, Classics, Documentaries, etc.)',
      addGenre: 'Add genre',
      addActor: 'Add actor',
      add: 'Add',
      actorNamePlaceholder: 'Actor name',
      genreNamePlaceholder: 'Genre name',
      movieTypePlaceholder: 'Movie type',
      minimumRating: 'Minimum Rating',
      minimumRatingDescription: 'Only recommendations with equal or higher rating will be shown',
      maxRecommendations: 'Maximum Recommendations',
      preferredLanguage: 'Preferred Language',
      spanishOption: 'Spanish',
      englishOption: 'English',
      anyLanguage: 'Any',
      contentRating: 'Content Rating',
      contentRatingG: 'G - General',
      contentRatingPG: 'PG - Parental Guidance',
      contentRatingPG13: 'PG-13 - Ages 13+',
      contentRatingR: 'R - Restricted',
      contentRatingNC17: 'NC-17 - Adults Only',
      ratingWeights: 'Rating Weights',
      userRatingWeight: 'User Rating',
      externalRatingWeight: 'External Rating',
      aiRatingWeight: 'AI Rating',
      weightsDescription: 'Weights must sum to 1.0 total. Current weight: {total}',
      saving: 'Saving...',
      savePreferences: 'Save Preferences',
      preferencesSaved: 'Preferences saved successfully to JSON file.',
      errorSaving: 'Error saving preferences',
      errorLoadingQuota: 'Error loading Groq quota',
      aiProvider: 'AI Provider',
      aiProviderTitle: 'AI Provider',
      aiProviderDescription: 'Groq (cloud)',
      aiProviderNote: 'All users use Groq to generate personalized recommendations.',
      groqApiKey: 'Groq API Key',
      groqApiKeyPlaceholder: 'Paste your API key here',
      groqApiKeyConfigured: '•••••••• (already configured in your account)',
      noApiKey: "Don't have an API key?",
      getApiKey: 'Get one free at Groq',
      getApiKeyNote: '(free, you just need to create an account)',
      saveApiKey: 'Save API Key',
      systemStatus: 'System Status',
      aiConnected: 'AI Connected',
      aiDisconnected: 'AI Disconnected',
      groqQuota: 'Groq Quota',
      checkingQuota: 'Checking Groq quota...',
      groqAvailable: 'Groq available',
      requestsRemaining: '{remaining} requests remaining{limit}',
      noRequestsAvailable: 'No requests available',
      groqNotAvailable: 'Groq not available',
      retryAfter: 'Try again in {seconds} seconds',
      paymentRequired: 'Groq payment plan update required',
      updateQuotaToSee: 'Click "Update quota" to see your availability',
      configureApiKeyToSee: 'Configure your Groq API key to see your available quota',
      checking: 'Checking...',
      updateQuota: 'Update quota',
      noAIServiceNote: 'Without AI service, new automatic recommendations cannot be generated.',
      preferencesSummary: 'Preferences Summary',
      actors: 'Actors',
      genres: 'Genres',
      movieTypes: 'Movie types',
      minimumRatingLabel: 'Minimum rating',
      maxRecommendationsLabel: 'Max. recommendations',
      languageLabel: 'Language',
      ratingLabel: 'Rating'
    },
    // Rating modal
    ratingModal: {
      rateContent: 'Rate content',
      editRating: 'Edit Rating',
      rate: 'Rate',
      howWouldYouRate: 'How would you rate this content?',
      ratingMethod: 'Rating method',
      slider: 'Slider',
      stars: 'Stars',
      buttons: 'Buttons',
      thumbs: 'Thumbs',
      notes: 'Notes (optional)',
      save: 'Save',
      cancel: 'Cancel',
      markAsWatched: 'Mark as Watched',
      genre: 'Main genre',
      selectGenre: 'Select genre',
      selectGenreAria: 'Select music genre',
      selectGenrePlaceholder: '— Select genre —',
      correctGenre: 'Correct or add the genre if the API put "Various".',
      selectCorrectGenre: 'Choose the genre from the list. If the API put "Various", select the correct one.',
      contentInfo: 'Content Information',
      type: 'Type',
      genreLabel: 'Genre',
      year: 'Year',
      artist: 'Artist',
      director: 'Director',
      actors: 'Actors',
      rating: 'Rating',
      ratingValue: 'Rating: {value}/10',
      selectRating: 'Select a rating',
      veryBad: 'Very bad',
      bad: 'Bad',
      regular: 'Regular',
      good: 'Good',
      excellent: 'Excellent',
      didYouLike: 'Did you like it?',
      liked: 'Liked',
      disliked: 'Disliked',
      description: 'Description',
      whyRecommended: 'Why is this recommended?',
      notesPlaceholder: 'Share your opinion about this content...'
    },
    // Landing Page
    landing: {
      brandName: 'Es tu mejor opción',
      navServices: 'Services',
      navEncaja: 'Encaja',
      navProducts: 'Products',
      navNews: 'News',
      navDomain: 'Domain',
      navProcess: 'Process',
      navWhy: 'Why us',
      navClients: 'Clients',
      navFaq: 'FAQ',
      navWhatsAppCta: 'WhatsApp',
      encajaBadge: '⭐ Star product',
      encajaByLine: 'A product by Estumejoropcion',
      encajaBrandName: 'Encaja',
      encajaTagline: 'Where your business fits',
      encajaDescription:
        'Inventory, customers, sales, and billing in one dashboard — built for SMBs in Paraguay, Bolivia, and LATAM.',
      encajaFeatureInventory: 'Inventory',
      encajaFeatureClients: 'Customers',
      encajaFeatureSales: 'Sales',
      encajaFeatureBilling: 'Billing',
      encajaPrimaryCta: 'Start 14-day free trial →',
      encajaSecondaryCta: 'View plans and pricing',
      encajaPlanPopularBadge: 'Most popular',
      encajaMockLabel: 'Live dashboard',
      encajaMockMetricOrders: 'Orders today',
      encajaMockMetricOrdersValue: '24',
      encajaMockMetricStock: 'Stock alerts',
      encajaMockMetricStockValue: '3',
      encajaMockMetricRevenue: 'Monthly sales',
      encajaMockMetricRevenueValue: '₲ 4.2M',
      heroBadge: '✨ Digital services for LATAM',
      heroTitle: 'Your web presence,',
      heroTitleHighlight: 'built to sell.',
      heroTitleSuffix: '',
      heroSubtitle:
        'We design websites, custom software, and automations that turn visits into customers. Fast, clear, and tailored to your business across Latin America.',
      heroTitleDomain: 'Register your domain and',
      heroTitleDomainHighlight: 'launch your brand',
      heroSubtitleDomain: 'We help you secure domains, set DNS, and activate your digital presence without friction.',
      heroSubtitleSupportDomain: 'Best for businesses that need a fast launch with a solid technical base.',
      heroTitleSoftware: 'Custom software to',
      heroTitleSoftwareHighlight: 'operate and scale',
      heroSubtitleSoftware: 'We build tailored systems for sales operations and commercial automation.',
      heroSubtitleSupportSoftware: 'From first workflow to phased roadmap with measurable KPIs.',
      heroPrimaryCta: 'Get started now →',
      heroSecondaryCta: 'Chat on WhatsApp',
      heroProofOne: '⚡ Fast response',
      heroProofTwo: '🌐 Clients across LATAM',
      heroProofThree: '🛡️ Crypto payments available',
      statDeliveredValue: '+50',
      statDeliveredLabel: 'Projects delivered',
      statReviewsValue: '9★',
      statReviewsLabel: 'Average reviews',
      statResponseValue: '24h',
      statResponseLabel: 'Response time',
      statCoverageValue: 'LATAM',
      statCoverageLabel: 'Regional coverage',
      heroMockBrowserUrl: 'your-brand.com',
      heroMockMetricVisits: 'VISITS',
      heroMockMetricVisitsValue: '12.4k',
      heroMockMetricClients: 'CLIENTS',
      heroMockMetricClientsValue: '+38%',
      heroMockMetricSpeed: 'SPEED',
      heroMockMetricSpeedValue: '98',
      heroMockLiveStatus: 'Live · Conversions today',
      fabWhatsAppAria: 'Open WhatsApp',
      faqBadge: 'FAQ',
      faqHeadingLead: 'We answer your',
      faqHeadingAccent: 'questions',
      faqQ1: 'How long does a website take?',
      faqA1:
        'A simple landing can go live within days. Custom builds depend on scope: we share a timeline and phased deliveries in the proposal.',
      faqQ2: 'How do payments work?',
      faqA2:
        'You can pay by card, bank transfer, or crypto. We agree price and milestones in writing so there are no surprises.',
      faqQ3: 'Do you work with clients across Latin America?',
      faqA3:
        'Yes. We support teams across the region in Spanish or English, whichever you prefer.',
      faqQ4: 'What happens after launch?',
      faqA4:
        'We train your team, provide WhatsApp support, and can continue with improvements and integrations as needed.',
      faqQ5: 'Can I migrate from another provider?',
      faqA5:
        'Yes. We plan migration for domain, hosting, and content to reduce downtime and protect SEO and data.',
      processBadge: 'How we work',
      processHeadingLead: 'A',
      processHeadingAccent: 'simple, clear',
      processHeadingTrail: 'process',
      processSubtitle:
        'No fluff. We move in short stages, keep communication on WhatsApp, and ship verifiable milestones.',
      processStep1Title: 'We discuss your project',
      processStep1Desc:
        'We learn your business, customers, and goals—without unnecessary jargon.',
      processStep2Title: 'A clear proposal',
      processStep2Desc: 'Scope, timeline, and fixed pricing. You know exactly what you get and when.',
      processStep3Title: 'Design and development',
      processStep3Desc:
        'UX/UI, SEO, and clean code. We share progress so you can validate every step.',
      processStep4Title: 'Launch and support',
      processStep4Desc:
        'We publish, train your team, and stay with you after go-live.',
      whyChooseBadge: 'Why choose us',
      whyChooseLine1a: 'The',
      whyChooseLine1b: 'best option',
      whyChooseLine2a: 'for your',
      whyChooseLine2b: 'business',
      whyChoose1Title: 'Fast delivery',
      whyChoose1Desc: 'Your site online in days, not months.',
      whyChoose2Title: 'Built to sell',
      whyChoose2Desc: 'UX, SEO, and copy focused on conversion.',
      whyChoose3Title: 'Human support',
      whyChoose3Desc: 'You talk to humans on WhatsApp—not bots.',
      whyChoose4Title: 'Flexible payments',
      whyChoose4Desc: 'Card, bank transfer, or crypto. You choose.',
      whyChoose5Title: 'Clear pricing',
      whyChoose5Desc: 'No surprises. What you see is what you pay.',
      whyChoose6Title: 'Across LATAM',
      whyChoose6Desc: 'We serve clients in Spanish and English across the region.',
      clientsBadge: 'Clients',
      clientsHeadingLead: 'What people say about',
      clientsHeadingAccent: 'our work',
      testimonial1Quote:
        'In under two weeks our store was online and selling. WhatsApp support is excellent.',
      testimonial1Name: 'Carolina M.',
      testimonial1Meta: 'Online boutique · Bolivia',
      testimonial1Initial: 'C',
      testimonial2Quote:
        'Very professional and clear on scope. The site loads fast and we get more inquiries.',
      testimonial2Name: 'Diego R.',
      testimonial2Meta: 'Law firm · Peru',
      testimonial2Initial: 'D',
      testimonial3Quote:
        'I love how it turned out. The design feels premium and I finally have a funnel that works.',
      testimonial3Name: 'Valentina S.',
      testimonial3Meta: 'Business coach · Colombia',
      testimonial3Initial: 'V',
      featureRealTitles: 'Content that ranks',
      featureAIPowered: 'Growing catalog',
      featureFreeForever: 'Regional support',
      ctaButton: 'Go to account & sign in',
      trustText: 'Browse technology and web business news without creating an account.',
      trustDomainOne: 'Final sale price shown before payment',
      trustDomainTwo: 'DNS and activation support included',
      trustDomainThree: 'Human support for registration',
      trustOffersOne: 'Clear scope and deliverables',
      trustOffersTwo: 'Roadmap defined by phases',
      trustOffersThree: 'Post-delivery support available',
      trustContactOne: 'Regional LATAM support',
      trustContactTwo: 'Fast commercial response',
      trustContactThree: 'No initial commitment required',
      trustFinalOne: 'First response within 24h',
      trustFinalTwo: 'Coverage for Paraguay, Bolivia, and LATAM',
      trustFinalThree: 'Flexible payment: traditional and crypto',
      heroCardBlog: 'News',
      heroCardStore: 'Store',
      heroCardLatam: 'LatAm',
      servicesTitle: 'What we offer',
      servicesBadge: 'What we offer',
      servicesHeadingLine1: 'Digital services',
      servicesHeadingAccent: 'end to end',
      servicesSubtitle:
        'For SMBs, professionals, and commercial teams. We prioritize speed, trust, and a clear offer to close more clients.',
      servicesMoreTitle: 'More digital solutions',
      productCardCta: 'View details',
      productCheckoutCta: 'Pay now',
      productViewDetailCta: 'View more',
      productPriceOnRequest: 'Price on request',
      productOfferBadge: 'Offer -{{percent}}%',
      productPriceMonthlySuffix: ' /mo',
      offerViewDetailCta: 'View details',
      productDomainsTitle: 'Domain registration',
      productDomainsDesc: 'Registration and renewal with DNS guidance and best practices for your brand.',
      productHostingTitle: 'Managed hosting (Hestia CP)',
      productHostingDesc: 'Copper, Silver, and Gold plans with Hestia panel (lighter than cPanel), Spanish support, and a free client course.',
      productVpsTitle: 'VPS server + Hestia CP',
      productVpsDesc: 'Managed VPS with Hestia preinstalled, technical support, and a free management course for your team.',
      productWebSimpleTitle: 'Simple websites',
      productWebSimpleDesc: 'Landings and sites ready to ship fast, with fixed scope and clear pricing.',
      productWebCustomTitle: 'Custom websites',
      productWebCustomDesc: 'UX/UI, SEO, and architecture built to turn visits into customers.',
      productSoftwareCustomTitle: 'Software & automation',
      productSoftwareCustomDesc: 'Integrations, dashboards, and bots that save hours for your sales team.',
      productAutomationTitle: 'Process automation',
      productAutomationDesc: 'We automate repetitive sales and operations tasks to reduce time and errors.',
      productDashboardsTitle: 'Operational dashboards',
      productDashboardsDesc: 'Business KPI dashboards for sales tracking, operations, and decision making.',
      productIntegrationsTitle: 'Business integrations',
      productIntegrationsDesc: 'We connect payments, CRM, ERP, and messaging so your data and workflows run as one system.',
      productSupportTitle: 'Support and SLA',
      productSupportDesc: 'Continuous support plans with agreed response times and iterative improvements.',
      regionNote: 'Commercial presence in Paraguay and Bolivia; projects across Latin America.',
      contactSectionTitle: 'Talk to us on WhatsApp',
      contactParaguayLine: 'Paraguay',
      contactBoliviaLine: 'Bolivia',
      leadFormTitle: 'Quick lead intake (minimal step 1)',
      leadNamePlaceholder: 'Your name',
      leadNeedWebsite: 'I need a sales website',
      leadNeedDomain: 'I need domain registration',
      leadNeedSoftware: 'I need custom software',
      leadStep1Cta: 'Continue',
      leadEmailPlaceholder: 'Email (optional)',
      leadCompanyPlaceholder: 'Company (optional)',
      leadNotesPlaceholder: 'Short context (optional)',
      leadBack: 'Back',
      leadSubmitCta: 'Send via WhatsApp',
      leadFormErrorNameRequired: 'Enter your name to continue.',
      leadWhatsappIntro: 'Hi, I want a quick commercial diagnosis.',
      leadFieldName: 'Name',
      leadFieldNeed: 'Need',
      leadFieldEmail: 'Email',
      leadFieldCompany: 'Company',
      leadFieldNotes: 'Notes',
      contactEnglishLine: 'English support is available by conversation.',
      contactEmailLine: 'Email:',
      contactParaguayDisplay: '0983 062031',
      contactBoliviaDisplay: '77499919',
      contactWhatsAppCta: 'Chat on WhatsApp',
      cryptoSectionTitle: 'We accept cryptocurrency',
      cryptoSectionBody:
        'Buy faster, confirm sooner, and move your project forward with less friction. Great for clients across LATAM.',
      cryptoSectionHighlight: 'Agile payments for domains, web services, and custom software.',
      cryptoSectionCta: 'I want to buy with crypto',
      latamClientsLine: 'We receive orders and work with clients across LATAM.',
      noCommitmentLine: 'Ask us with no commitment: we are your best option.',
      offersTitle: 'High-conversion packages',
      offersSubtitle: 'Inspired by market leaders: simple plans, clear value, and direct CTAs for faster decisions.',
      offersDiscountTitle: 'Discounted offers',
      offersDiscountLead: 'Strikethrough reference price — grab the active discount.',
      offerStarterBadge: 'Quick start',
      offerStarterTitle: 'Simple 5-section landing',
      offerStarterDesc: 'Standard 5-section landing, domain and hosting (1 year), plus 5 business mailboxes. Ready to capture leads.',
      offerStarterPriceHint: 'USD 299 · fast-lane bundle',
      offerStarterCta: 'Pay now',
      offerStarterBestFor: 'Businesses that need fast digital presence',
      offerStarterUpgradeTo: 'Growth',
      offerBusinessBadge: 'Most popular',
      offerBusinessTitle: 'Business website + automation',
      offerBusinessDesc: 'Multi-page site with service sections, WhatsApp integration, and lead funnel structure.',
      offerBusinessPriceHint: 'Best for scaling sales',
      offerBusinessCta: 'Book a diagnosis call',
      offerBusinessBestFor: 'Teams with active sales process',
      offerBusinessUpgradeTo: 'Scale',
      offerScaleBadge: 'Scale',
      offerScaleTitle: 'Scale: advanced operations and funnel',
      offerScaleDesc: 'Conversion optimization, commercial automation, and continuous improvements through iterations.',
      offerScalePriceHint: 'For sustainable data-driven growth',
      offerScaleCta: 'I want to scale',
      offerScaleBestFor: 'Growing businesses with quarterly goals',
      offerScaleUpgradeTo: 'Custom',
      offerCustomBadge: 'Custom',
      offerCustomTitle: 'Custom software system',
      offerCustomDesc: 'Tailored platform design and development: admin panel, roles, workflows, and modules for your operation.',
      offerCustomPriceHint: 'Phased roadmap + support',
      offerCustomCta: 'Request proposal',
      offerCustomBestFor: 'Operations with specific requirements and high complexity',
      offerCustomUpgradeTo: 'Phased evolution plan',
      offerBestForLabel: 'Best for',
      offerUpgradeLabel: 'Next upgrade',
      proofTitle: 'Real proof of outcomes',
      proofSubtitle: 'Metrics, trust signals, and delivery evidence to support lower-risk decisions.',
      proofMetricOneValue: '+34%',
      proofMetricOneLabel: 'Average lift in first-contact conversion rate',
      proofMetricTwoValue: '-27%',
      proofMetricTwoLabel: 'Reduction in commercial response time',
      proofMetricThreeValue: '4.8/5',
      proofMetricThreeLabel: 'Reported satisfaction during project delivery',
      proofTestimonialOneQuote: '"In a few weeks, we moved from scattered chats to a clear web + WhatsApp sales flow."',
      proofTestimonialOneAuthor: 'Commercial team · B2B services (PY)',
      proofTestimonialTwoQuote: '"They helped us structure offer, pricing, and follow-up; we now close deals with less friction."',
      proofTestimonialTwoAuthor: 'Leadership · Digital business (BO)',
      proofAuthorityOne: 'Conversion-oriented methodology',
      proofAuthorityTwo: 'Scalable technical architecture',
      proofAuthorityThree: 'Phased implementation with KPIs',
      proofAuthorityFour: 'LATAM support and continuous improvement',
      featuresTitle: 'Why work with us',
      featuresSubtitle: 'We combine content, SEO, and services so your brand stands on solid tech.',
      featurePersonalizedTitle: 'Technology and web business news',
      featurePersonalizedDesc: 'Useful content about domains, hosting, security, and digital growth for companies.',
      featureRateTitle: 'Service store',
      featureRateDesc: 'Domains, hosting, VPS, and web development in one ecosystem (e-commerce rolling out).',
      featureTrackTitle: 'SEO and analytics',
      featureTrackDesc: 'Metadata, clean URLs, and structure built for search and social.',
      featureSearchTitle: 'Regional reach',
      featureSearchDesc: 'LatAm-first: language, local context, and regional contact channels.',
      featureControlTitle: 'Clear scopes',
      featureControlDesc: 'Defined deliverables for simple sites and roadmaps for custom builds.',
      featureOwnAITitle: 'Ongoing partnership',
      featureOwnAIDesc: 'We support your growth with continuous improvements, support, and product evolution.',
      howItWorksTitle: 'How to get started',
      howItWorksSubtitle: 'Three steps',
      step1Title: 'Read and explore',
      step1Desc: 'Explore technology news and services to spot practical opportunities for your business.',
      step2Title: 'Pick a service',
      step2Desc: 'Domain, website, or custom software: we guide you based on your stage and business goals.',
      step3Title: 'Talk to us',
      step3Desc: "If you're not fully sure what you need, start here: we help you define the best next move.",
      step3Cta: 'Start here',
      ctaTitle: 'Ready to sell more?',
      ctaSubtitle: 'Create your account to coordinate your website or custom system project. You can also continue with Google.',
      finalCtaLine1: 'Ready to take your business',
      finalCtaLine2: 'to the next level?',
      finalCtaSubtitle:
        'Tell us your idea on WhatsApp. Within 24 hours you will have a clear proposal with timeline and pricing.',
      finalCtaPrimary: 'Chat on WhatsApp →',
      finalCtaSecondary: 'Email us',
      mobileStickyPrimaryCta: 'WhatsApp',
      mobileStickySecondaryCta: 'Email',
      footerWordEstu: 'Your',
      footerWordMejor: 'best',
      footerWordOpcion: 'option',
      footerBrandFull: 'Your best option',
      footerCopyrightLine: '© 2026 Es tu mejor opción · Made with care across LATAM',
      footerEncajaPlans: 'Encaja plans',
      footerEncajaPanel: 'Encaja dashboard',
      footerText: 'Es tu mejor opción · News and digital services for Latin America',
      footerCopyright: '© 2026 Es tu mejor opción',
      latestNewsTitle: 'Latest news',
      latestNewsReadMore: 'Read more',
      latestNewsNone: 'No recent news.',
      allNewsLink: 'See all news here',
      domainSearchTitle: 'Find your domain',
      domainSearchSubtitle:
        'Enter your brand or a name (with or without an extension). We show several extensions with availability and an indicative registration price.',
      domainSearchTrustLine: 'Fast activation + DNS support to guide your full setup process.',
      domainSearchPlaceholder: 'example.com or my-brand',
      domainSearchButton: 'Search',
      domainSearchLoading: 'Checking…',
      domainSearchErrorRequired: 'Enter a domain name.',
      domainSearchErrorUnconfigured:
        'Domain lookup is not available right now. Try again later or contact us.',
      domainSearchErrorFailed: 'Could not check the domain. Please try again.',
      domainSearchErrorInvalid: 'Invalid name. Use letters, numbers, and hyphens (one label, e.g. my-brand).',
      domainSuggestionsTitle: 'Options by extension',
      domainSuggestionsFor: 'For the name',
      domainSuggestionsHeadDomain: 'Domain',
      domainSuggestionsHeadStatus: 'Status',
      domainSuggestionsHeadPrice: 'Selling price',
      domainSuggestionsHeadAction: 'Action',
      domainSuggestionsFootnote:
        'Final selling price per domain and registration period.',
      domainNextStepNote:
        'Select a row and continue to your account to coordinate registration, DNS, and the rest of your project.',
      domainContinueCta: 'Continue to payment',
      domainSelectFirst: 'Select a domain in the table',
      domainRowBuyNow: 'Buy now',
      domainUnavailableBadge: 'Not available',
      domainRowSelectLabel: 'Select {domain}',
      domainPremiumBadge: 'Premium',
      domainSpecialOfferBadge: 'Special offer',
      domainPriceQuote: '{amount} {currency} / {months} mo',
      domainPriceQuoteYear: '{amount} {currency} per year',
      domainPriceSimple: '{amount}',
      domainStatusAvailable: 'Available for registration',
      domainStatusPrereg: 'Pre-registration or special phase',
      domainStatusReserved: 'Releasable reservation (subject to terms)',
      domainStatusTaken: 'Not available',
      domainStatusError: 'Could not verify',
      domainStatusSuspended: 'Registration suspended',
      domainStatusBlocked: 'Not allowed (blocked list)',
      domainStatusUnknown: 'Unknown status',
      domainPremiumHint: 'Premium domain: final price may vary.',
      // Auth errors
      errorGoogleNotConfigured: 'Google OAuth is not configured on the server.',
      errorGoogleAuthFailed: 'Error authenticating with Google. Please try again.',
      errorNoUser: 'Could not create or retrieve user.',
      errorCallback: 'Error in the authentication process.',
      errorSession: 'Error saving session.',
      errorSignIn: 'Error signing in with Google.',
      errorSignInGeneric: 'Error signing in.',
      errorConnection: 'Connection error.'
    },
    // All news page
    allNews: {
      title: 'All news',
      backToHome: 'Back to home',
      loading: 'Loading news...',
      noPosts: 'No published news.',
      readMore: 'Read more'
    },
    conversionProof: {
      title: 'Conversion proof',
      pointOne: 'Cases with measurable gains in contact rate and sales response.',
      pointTwo: 'Implementation focused on clear deliverables and business priorities.',
      pointThree: 'Post-launch follow-up for continuous optimization.',
      badgeOne: 'Measurable outcomes',
      badgeTwo: 'LATAM support',
      cta: 'See more evidence'
    },
    productPage: {
      brandSuffix: 'Es tu mejor opción',
      errorLoad: 'Could not load the service page.',
      notFound: 'This service or package was not found.',
      backHome: 'Back to home',
      categoryLabel: 'Category',
      noCategory: 'No category',
      defaultDescription: 'Digital service focused on commercial outcomes and sustainable growth.',
      summaryTitle: 'Package summary',
      includesTitle: 'Your landing includes 5 sections',
      infrastructureTitle: 'Infrastructure included',
      conditionsTitle: 'Clear terms',
      problemTitle: 'What you get',
      problemBody: 'Launch online without friction: a conversion-focused landing plus everything needed to go live.',
      processTitle: 'What happens after payment',
      processOne: 'We confirm your order in your account and request brand, copy, and references.',
      processTwo: 'We design and build the landing with the 5 agreed sections.',
      processThree: 'We activate domain, hosting, and mailboxes; you receive access and a checklist.',
      deliverablesTitle: 'Deliverables',
      deliverableOne: 'Published responsive landing with contact CTA.',
      deliverableTwo: 'Domain and hosting active for the first year (per provider).',
      deliverableThree: '5 business email accounts on your domain.',
      timelineTitle: 'Estimated timeline',
      timelineBody: 'Typical delivery within a few business days after we receive your content.',
      priceLabel: 'Package price',
      priceNote: 'One-time USD payment. Domain, hosting, and email renewals are quoted separately after year one.',
      payNowCta: 'Pay now',
      whatsappCta: 'Contact us',
      whatsappPrefill: 'Hi, I have a question about the package: {productName}',
      noPaymentMethods: 'Online payment is unavailable right now. Contact us on WhatsApp.',
      checkoutNotEnabled: 'Online checkout is not enabled for this product. Contact us on WhatsApp to purchase.',
      loginHint: 'Sign in to pay and save your purchase to your account.',
      starterSection1: 'Hero with value proposition and contact button',
      starterSection2: 'Services: what you offer and to whom',
      starterSection3: 'Benefits and differentiators',
      starterSection4: 'Social proof (testimonials, logos, or cases)',
      starterSection5: 'Contact with form and final CTA',
      starterInfra1: 'Domain registration (1 year)',
      starterInfra2: 'Shared hosting (1 year)',
      starterInfra3: '5 business email accounts on your domain',
      starterCondition1: 'Fixed package price as shown on this page',
      starterCondition2: 'You provide copy and logos, or we quote copywriting as an add-on',
      starterCondition3: 'Renewals after year one are quoted separately',
      starterCondition4: 'We contact you after payment to start the project',
      genericInclude1: 'Implementation aligned to the published package scope',
      genericInclude2: 'Documented deliverables and launch support',
      genericInclude3: 'Agreed adjustments within the published scope',
      relatedTitle: 'Related services',
      relatedCta: 'View details',
      breadcrumbHome: 'Home',
      breadcrumbServices: 'Services',
      starterTagline: 'Everything to launch online: 5-section landing, domain, hosting, and business email.',
      trustBadge1: 'Delivery in a few business days',
      trustBadge2: 'LATAM support',
      trustBadge3: 'Fixed published price',
      detailsTitle: 'Package details',
      galleryAltHero: 'Landing hero preview',
      galleryAltSections: 'Services and benefits sections',
      galleryAltMobile: 'Mobile responsive preview',
      galleryAltInfra: 'Domain, hosting, and email',
      galleryAltFull: 'Full page preview',
      galleryAltDefault: 'Service preview',
      encajaMonthlyPriceNote: 'Monthly subscription in USD. Start your 14-day trial on Encaja; plan billing is handled by Estumejoropcion.',
      encajaTrialCta: 'Start 14-day free trial on Encaja',
      encajaContractCta: 'Subscribe to this plan',
      encajaSalesCta: 'Request a quote',
      encajaLoginHint: 'The free trial starts at encaja.estumejoropcion.com. To activate a paid plan, contact us on WhatsApp.',
      encajaCondition1: 'Monthly price as published on this page',
      encajaCondition2: '14-day free trial on the Encaja dashboard',
      encajaCondition3: 'Billing and commercial support via Estumejoropcion',
      encajaStarterFeature1: 'Product catalog and stock control',
      encajaStarterFeature2: 'Inventory movements and one warehouse',
      encajaStarterFeature3: 'Up to 2 dashboard users',
      encajaBusinessFeature1: 'Everything in Starter + CRM module',
      encajaBusinessFeature2: 'Customers, contacts, and sales follow-up',
      encajaBusinessFeature3: 'Up to 5 users',
      encajaProfessionalFeature1: 'Integrated inventory, CRM, and billing',
      encajaProfessionalFeature2: 'Multi-warehouse and full commercial ops',
      encajaProfessionalFeature3: 'Up to 15 users — recommended plan',
      encajaEnterpriseFeature1: 'Custom limits and modules',
      encajaEnterpriseFeature2: 'SLA and dedicated onboarding',
      encajaEnterpriseFeature3: 'Integrations per discovery',
      encajaEnterpriseCondition1: 'Scope and price per commercial proposal',
      encajaEnterpriseCondition2: 'Guided onboarding with Estumejoropcion',
      encajaEnterpriseCondition3: 'Priority support per contract'
    },
    checkoutProduct: {
      title: 'Package checkout',
      intro:
        'Price and scope match the product page. After payment we activate your project from your account.',
      backHome: 'Back to home',
      backProduct: 'Back to product',
      missingSlug: 'Product missing from URL. Go home and choose a package.',
      loginRequiredTitle: 'Sign in to pay',
      loginRequiredBody: 'You need an account to complete payment and view your purchase.',
      goToLogin: 'Go to sign in',
      loadingQuote: 'Loading price…',
      labelProduct: 'Product',
      labelAmount: 'Total',
      amountUsd: 'USD {amount}',
      quoteDisclaimer: 'This is the published price for this package. No surprises at checkout.',
      payWithPayPal: 'Pay with PayPal',
      payWithBinanceWallet: 'Pay with USDT deposit (Binance)',
      noGateways: 'Payment methods are not configured on the server.',
      payPalCancelled: 'You cancelled PayPal payment. You can try again.',
      binanceInstructionsTitle: 'Send USDT to this deposit address',
      binanceScanQr: 'Scan the QR code with Binance or another compatible wallet',
      binanceDownloadQr: 'Download QR',
      binanceQrHint: 'You can also save the image and scan it from your phone gallery.',
      binanceReference: 'Internal reference:',
      binanceNetwork: 'Network:',
      binanceAmount: 'Exact amount (USDT):',
      binanceAmountHint:
        'Approximately your USD purchase ({usd}). Must match the amount shown to link payment.',
      binanceAddress: 'Address:',
      binanceTag: 'Memo / Tag:',
      binancePolling: 'We detect the deposit automatically. This page updates when it arrives.',
      successTitle: 'Payment confirmed',
      successBody: 'We registered your purchase of {productName}.',
      fulfillmentNote:
        'We will contact you shortly for brand, copy, and domain details. View the order in your account.',
      goToAccount: 'Go to my account',
      errorQuote: 'Could not load the product. Try again.',
      errorProductNotFound: 'This product is not available.',
      errorPriceUnavailable: 'No price available for this product.',
      errorPayPalStart: 'Could not start PayPal.',
      errorPayPalCapture: 'Could not confirm PayPal payment.',
      errorBinanceStart: 'Could not prepare deposit address.'
    },
    // Groq Tutorial
    groqTutorial: {
      back: 'Back',
      title: 'How to Set Up Groq API for Real Recommendations',
      subtitle: 'Learn how to get your free Groq API key in less than 5 minutes',
      whyNeededTitle: 'Why do you need Groq?',
      whyNeeded: 'To generate real and personalized recommendations, we need access to advanced AI models. Groq provides this service quickly and affordably.',
      freeTitle: 'Free Plan Available',
      freeDescription: 'Groq offers a generous free plan with daily limits. For personal use, it\'s more than enough and doesn\'t require a credit card.',
      step1Title: 'Create a Groq Account',
      step1Description: 'Visit the Groq console and create a free account. You can use your Google account to sign in quickly.',
      openConsole: 'Open Groq Console',
      step1Note: 'Note: Registration is completely free and doesn\'t require a credit card.',
      step2Title: 'Generate Your API Key',
      step2Description: 'Once inside the console, go to the API Keys section and create a new key. Copy it immediately because it will only be shown once.',
      step2Code: 'gsk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      copy: 'Copy',
      copied: 'Copied!',
      step3Title: 'Configure in Es tu mejor opción',
      step3Description: 'Now that you have your API Key, follow these steps to configure it:',
      step3Item1: 'Go to the Settings section in your dashboard',
      step3Item2: 'Paste your Groq API Key in the corresponding field',
      step3Item3: 'Save the changes and you\'re done! You can now generate real recommendations',
      goToSettings: 'Go to Settings',
      faqTitle: 'Frequently Asked Questions',
      faq1Question: 'Is it really free?',
      faq1Answer: 'Yes, Groq offers a free plan with generous limits. For personal recommendation use, it\'s more than enough. You would only pay if you exceed the free plan limits.',
      faq2Question: 'Do I need a credit card?',
      faq2Answer: 'No, Groq\'s free plan doesn\'t require a credit card. You can start using it immediately after creating your account.',
      faq3Question: 'Is it safe to share my API Key?',
      faq3Answer: 'Your API Key is stored securely in your account and only you can see it. Never share it publicly. In Es tu mejor opción, your key is stored encrypted and only used to generate blog drafts.',
      faq4Question: 'What happens if I exceed the free limits?',
      faq4Answer: 'Groq has very generous limits. If you exceed them, you can set spending limits in your Groq account or consider upgrading to a paid plan. Prices are very affordable (starting at $0.05 per million tokens).',
      readyTitle: 'Ready to get started?',
      readyDescription: 'Configure your Groq API Key now and start receiving real and personalized recommendations.',
      configureNow: 'Configure Now',
      learnMore: 'Learn more about Groq',
      linkText: 'View full tutorial'
    },
    // Common messages
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      search: 'Search',
      filter: 'Filter',
      noResults: 'No results found',
      noData: 'No data available',
      note: 'Note',
      logout: 'Logout',
      aiServiceNotAvailable: 'AI service is not available. New automatic recommendations cannot be generated.',
      learnHowToConfigure: 'Learn how to configure Groq API for real recommendations →',
      errorLoadingData: 'Error loading data.',
      aiAvailable: 'AI available',
      aiNotAvailable: 'AI not available',
      errorGeneratingRecommendations: 'Error generating recommendations',
      generatingRecommendations: 'Generating recommendations...',
      errorMarkingWatched: 'Error marking content as watched.',
      errorUpdatingPreferences: 'Error updating preferences.',
      errorLoadingMovies: 'Error loading movies',
      errorLoadingSeries: 'Error loading series',
      errorLoadingMusic: 'Error loading music',
      errorMarkingListened: 'Error marking as listened',
      checkingAuth: 'Checking session...',
      // Blog
      blog: {
        loading: 'Loading...',
        error: 'Error',
        postNotFound: 'Article not found',
        errorLoading: 'Error loading article',
        backToHome: 'Back to home',
        views: 'views',
        rateThisContent: 'Rate this content',
        rateBusiness: 'Rate this business content',
        rateTech: 'Rate this tech content',
        rateNews: 'Rate this news article',
        goToRate: 'Go to rate',
        adminTitle: 'Content management',
        newPost: 'New article',
        editPost: 'Edit article',
        title: 'Title',
        category: 'Content type',
        status: 'Status',
        publishedAt: 'Published At',
        actions: 'Actions',
        noPosts: 'No articles yet',
        published: 'Published',
        draft: 'Draft',
        viewPost: 'View article',
        edit: 'Edit',
        delete: 'Delete',
        confirmDelete: 'Are you sure you want to delete this article?',
        errorDeleting: 'Error deleting article',
        errorSaving: 'Error saving article',
        saving: 'Saving...',
        save: 'Save',
        excerpt: 'Excerpt',
        excerptPlaceholder: 'Short summary of the article (optional)',
        excerptHelp: 'This text will appear in previews and meta tags',
        content: 'Content',
        contentPlaceholder: 'Write article content in HTML...',
        contentHelp: 'You can use HTML to format the content',
        featuredImage: 'Featured Image',
        imageUrlPlaceholder: 'Featured image URL',
        uploadImage: 'Upload image',
        uploadingImage: 'Uploading…',
        uploadImageHelp: 'Upload an image or paste a URL',
        seoSettings: 'SEO Settings',
        metaTitle: 'Meta Title',
        metaTitlePlaceholder: 'Title for SEO (optional, defaults to article title)',
        metaTitleHelp: 'If left empty, the article title will be used',
        metaDescription: 'Meta Description',
        metaDescriptionPlaceholder: 'Description for SEO (optional)',
        metaDescriptionHelp: 'Description that will appear in search results',
        metaKeywords: 'Meta Keywords',
        metaKeywordsPlaceholder: 'Keywords separated by commas',
        metaKeywordsHelp: 'Keywords for SEO (optional)',
        titlePlaceholder: 'Article title',
        adminAccessDenied: 'Access denied. Admin, manager, or sales role required.',
        accessDeniedOops: 'Oops!',
        accessDeniedTitle: "You shouldn't be here!",
        accessDeniedMessage: "This area is for staff roles only (admin, manager, or sales). Don't worry, we'll get you back on track in a sec.",
        accessDeniedRedirect: 'Taking you to your account in {seconds} seconds…',
        accessDeniedGoToAccount: 'Go to my account',
        generateWithAI: 'Generate with AI',
        generateTopicLabel: 'Article topic',
        generateTopicPlaceholder: 'E.g.: how an SMB can increase sales with website + WhatsApp',
        generateTopicHelp: 'Describe the business objective (leads, conversion, online presence, automation, etc.).',
        generating: 'Generating article...',
        generateError: 'Error generating article.',
        generateSuccess: 'Draft created. Review, add image if you want, and publish.',
        generateLanguageLabel: 'Article language',
        generateLanguageEs: 'Spanish',
        generateLanguageEn: 'English',
        generateLanguageHelp: 'Generate the same topic in another language for ES/EN versions.',
        usageTitle: 'Generation usage (Groq)',
        promptSentTitle: 'Prompt sent to LLM',
        promptSentSystem: 'System prompt',
        promptSentUser: 'User prompt',
        relatedTitleLabel: 'Real title (for Rate link)',
        relatedTitlePlaceholder: 'E.g. The Godfather',
        relatedYearLabel: 'Year (for Rate link)',
        relatedYearPlaceholder: '1972',
        usagePromptTokens: 'Prompt tokens',
        usageCompletionTokens: 'Completion tokens',
        usageTotalTokens: 'Total tokens',
        usageModel: 'Model',
        usageResponseTime: 'Response time',
        postLanguage: 'Article language (URL)',
        postLanguageHelp: 'ES: /noticias/actualidad|tecnologia|negocios/... — EN: /news/news|tech|business/...',
        searchPlaceholder: 'Search by title, slug or excerpt...',
        searchButton: 'Search',
        filterStatus: 'Status',
        filterStatusAll: 'All',
        filterStatusPublished: 'Published',
        filterStatusDraft: 'Drafts',
        filterCategory: 'Type',
        filterCategoryAll: 'All types',
        searchImagesOnGoogle: 'Search large images on Google',
        page: 'Page',
        prev: 'Previous',
        next: 'Next',
        showingCount: 'Showing {from}-{to} of {total}',
        mightAlsoLike: 'You might also like'
      },
      loadingModalDefaultTitle: 'Generating recommendations...',
      loadingModalDefaultMessage: 'Connecting with AI server...',
      loadingModalCloseAndReload: 'Close and reload',
      loadingModalConnecting: 'Connecting with Open WebUI...',
      loadingModalAnalyzing: 'Analyzing your preferences...',
      loadingModalGenerating: 'Generating personalized recommendations...',
      loadingModalDetecting: 'Detecting duplicates...',
      loadingModalSaving: 'Saving updated recommendations...'
    }
  }
};

// Función para obtener traducción
export const t = (key, lang = 'es') => {
  // Validar y normalizar el idioma
  if (!lang || (lang !== 'es' && lang !== 'en')) {
    lang = 'es'; // Fallback a español por defecto
  }
  
  const keys = key.split('.');
  let value = translations[lang];
  
  // Si no existe el idioma, usar español como fallback
  if (!value) {
    value = translations.es;
  }
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Fallback a español si no encuentra la clave en el idioma actual
      value = translations.es;
      for (const k2 of keys) {
        if (value && typeof value === 'object' && k2 in value) {
          value = value[k2];
        } else {
          // Si aún no encuentra, devolver la clave
          logWarn(`Translation key not found: ${key} (lang: ${lang})`);
          return key;
        }
      }
      break;
    }
  }
  
  return typeof value === 'string' ? value : key;
};

// Función para formatear texto con variables
export const tf = (key, vars = {}, lang = 'es') => {
  let text = t(key, lang);
  Object.keys(vars).forEach(key => {
    text = text.replace(`{${key}}`, vars[key]);
  });
  return text;
};

export default translations;
